import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { WIND_DIR, WIND_GUST } from '../engine/wind';
import { NODES, TERRAIN, BIOMES } from '../config';
import { makeEzTreeParts } from '../engine/eztrees';
import { creasedNormals } from '../engine/creased';
import { applyStoneDetail } from '../engine/stonedetail';
import { terrainHeight, terrainSlope } from '../engine/terrain';
import { forestDensity, biomeAt } from '../engine/biome';
import { applyInteriorDark } from '../engine/interior';
import { applyCloudShadow } from '../engine/clouds';
import type { ItemId } from './inventory';
import type { Box, BoxPool } from '../engine/collision';

const UP = new THREE.Vector3(0, 1, 0);
const FWD = new THREE.Vector3(0, 0, 1);
const TMP_A = new THREE.Vector3();
const TMP_B = new THREE.Vector3();
const TMP_Q = new THREE.Quaternion();
const TMP_SCALE = new THREE.Vector3();
const ZERO_SCALE_M = new THREE.Matrix4().makeScale(0, 0, 0); // degenerate matrix: hides a foliage instance (leafless sapling)
const GLOW_TEX = makeGlowTexture();

// Shared LOD cross-fade state (one set of uniform objects driving every tree material -- the 7 billboard
// materials AND the 4 full-3D tree materials -- so a single update steers them together). The billboard
// dithers OUT and the full 3D tree dithers IN over [near..far] of `ctr` (the player/focus XZ, the same
// metric the streamer uses), with COMPLEMENTARY dither thresholds so the two always tile to a solid tree.
const IMP_FADE = {
  near: { value: NODES.impostorNear },
  far: { value: NODES.impostorFar },
  day: { value: 1 }, // 0 night .. 1 day -- dims the unlit billboards like the lit 3D trees
  ctr: { value: new THREE.Vector2() }, // focus (player) world XZ; set each frame in NodeField.update
  tint: { value: new THREE.Color(0.82, 0.85, 0.78) }, // per-frame sun+ambient colour so billboards match the lit 3D trees
};
// Common GLSL: the hash dither + the discard that fades the FULL 3D tree IN as it nears the player. It is the
// exact complement of the billboard's discard (billboard keeps dither<=s, 3D keeps dither>=s, same s) so every
// screen pixel shows exactly one of the two through the band. Keyed on the instance's XZ distance to ctr.
const IMP_DITHER_GLSL = 'float impDither(vec2 p){ return fract(sin(dot(floor(p), vec2(12.9898, 78.233))) * 43758.5453); }';
// Chain a complementary dither fade-IN onto a full-3D tree material (trunk/leaf/cactus/palm). Preserves any
// existing onBeforeCompile (leaf-wind sway, interior darken) and cache key. Prepends its decls so it does not
// depend on #include <common> still being present after earlier injections replaced it.
type ImpCompilable = THREE.Material & {
  onBeforeCompile: (shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) => void;
  customProgramCacheKey?: () => string;
};
function applyImpostorFadeIn(mat: ImpCompilable): void {
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.onBeforeCompile = (shader, renderer) => {
    if (prev) prev.call(mat, shader, renderer);
    shader.uniforms.uImpNear = IMP_FADE.near;
    shader.uniforms.uImpFar = IMP_FADE.far;
    shader.uniforms.uImpCtr = IMP_FADE.ctr;
    shader.vertexShader = 'uniform vec2 uImpCtr;\nvarying float vImpFD;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      #ifdef USE_INSTANCING
        vImpFD = distance((modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz, uImpCtr);
      #else
        vImpFD = 0.0; // non-instanced (the /admin LOD-capture display tree) never fades -> always solid
      #endif`,
    );
    shader.fragmentShader = `uniform float uImpNear;\nuniform float uImpFar;\nvarying float vImpFD;\n${IMP_DITHER_GLSL}\n` + shader.fragmentShader.replace(
      '#include <clipping_planes_fragment>',
      `#include <clipping_planes_fragment>
       // Fade the full 3D tree IN toward the player; complement of the billboard's fade-OUT (same s, same dither).
       if (impDither(gl_FragCoord.xy) < smoothstep(uImpNear, uImpFar, vImpFD)) discard;`,
    );
  };
  mat.customProgramCacheKey = () => (prevKey ? prevKey.call(mat) : mat.type) + '|impfadein1';
}

// Harvestable resource nodes: trees (wood) and ore rocks (stone, metal, sulfur). Each kind
// is one InstancedMesh (the spec asks for instanced low-poly). Every instance carries
// gameplay data and a target item. Hitting a node shrinks it; when depleted its instance is
// hidden (zero-scaled) and the caller spawns a loot container at its position.

interface NodeInst {
  mesh: THREE.InstancedMesh;
  foliage?: THREE.InstancedMesh; // trees: a second instanced mesh (alpha leaf cards) sharing the matrix
  apples?: THREE.InstancedMesh; // apple oak/aspen: a third instanced mesh (red canopy clusters) sharing the SAME slot as foliage
  hasApples?: boolean; // this specific tree bears apples (position-hashed at creation; the apples mesh zero-scales the rest)
  isPalm?: boolean; // a coconut palm (bears coconuts) -- used by the wind-gust fruit-drop
  impostor?: THREE.InstancedMesh; // trees: the far-LOD low-poly mesh (fixed slot = index); hidden when chopped
  index: number;
  id?: number; // stable wire id, assigned lazily the first time a node is targeted (spec section 19)
  pos: THREE.Vector3;
  quat: THREE.Quaternion;
  scale: number;
  remaining: number; // resource "units" left (1 unit == 1 baseline rock-swing); a tool extracts mult units/swing
  maxHits: number;   // total units == baseline rock-swings to deplete; total yield == perHit * maxHits
  perHit: number;    // primary resource granted PER UNIT (per-node, so a node's total == perHit * maxHits)
  secondaryItem?: ItemId; // metal nodes also drop stone: the byproduct item
  secondaryPerHit?: number; // byproduct granted per unit (secondary total == secondaryPerHit * maxHits)
  alive: boolean;
  reviveAt?: number; // MP: wall-clock seconds (Date.now()/1000) when this server-felled node respawns (shared across clients)
  item: ItemId;
  spot: THREE.Vector3; // local offset (pre-scale) of the glowing sweet spot
  spotKind: 'boulder' | 'tree';
  inner: number; // ring inner radius this node scatters within (so regenerate re-scatters it the same way)
  fullScale: number; // mature size; node.scale ramps up to this while a seeded tree grows
  growing: boolean;  // a seeded sapling still growing toward fullScale
  plantDay: number;  // in-game day it was seeded (growth runs over NODES.regrowDays)
  cell: number;      // packed key of the spatial-grid bucket this node currently sits in (-1 = not indexed)
  slot: number;      // current instance slot in its mesh's COMPACTED draw range (trees: -1 when culled; ore: == index, fixed)
  trunkColor?: THREE.Color;   // stored per-tree trunk tint, re-uploaded each stream pass (trees only)
  foliageColor?: THREE.Color; // stored per-tree foliage tint, re-uploaded each stream pass (trees with foliage)
  culled?: boolean; // per-frame frustum cull: this streamed tree is outside the view, hidden (matrix scaled to 0)
  swayT?: number;   // seconds left of the hit-wobble (trees)
  fallT?: number;   // seconds into the fall-and-fade death animation (>=0 while falling, else undefined/-1)
  falling?: boolean; // dead tree mid fall/fade -- drawn at full scale, animation owns its matrix
  fallDir?: number;  // horizontal direction the tree sways/falls toward (radians)
  dirty?: boolean;  // tree differs from the deterministic layout (hit/chopped/recycled) -> world save includes it
}

// World-save shapes. TREES are keyed by index into treeNodes (the deterministic layout gives the same
// order every boot, and a recycled seed MOVES, so its position cannot be the key -- the saved x/y/z is
// the payload that restores that move). ORE is keyed by item+position instead: positions never change
// and survive index reshuffles from config tweaks. Tree growth stores plantDay ABSOLUTE because the
// day clock itself persists (clockSeconds), same convention as farming's plantedDay.
export interface SavedTree { i: number; x: number; y: number; z: number; s: number; rem: number; g?: 1; d?: number; }
export interface SavedOre { k: string; rem: number; }
export interface SavedNodes { trees: SavedTree[]; ore: SavedOre[]; }
function oreKey(item: string, x: number, z: number): string { return `${item}|${Math.round(x * 10)},${Math.round(z * 10)}`; }

export interface HarvestResult {
  item: ItemId;
  amount: number;
  secondary?: { item: ItemId; amount: number }; // metal nodes: the stone byproduct granted on the same swing
  apples?: number; // felled apple tree: apples to scatter at the stump for the group (see localBackend.gather)
  depleted: boolean;
  pos: THREE.Vector3;
}

const _cullFrustum = new THREE.Frustum();
const _cullMat = new THREE.Matrix4();
const _cullSphere = new THREE.Sphere();

const NODE_SEED = 20260617; // fixed so regenerate lays out the same nodes for the same terrain
// A felled tree / mined-out rock respawns SAME-SPOT after this many seconds, on a clock shared by the server
// (world.ts NODE_RESPAWN_MS = 13_020_000) so every client's respawn lands at the same wall moment. This replaces
// the old client-local sapling regrow for FELLED trees (which drifted per client). Seedlings are OFF (owner: Rust-
// like same-spot respawn only). ~NODES.regrowDays (7) * a ~1860s game-day. KEEP IN SYNC with the server constant.
export const NODE_RESPAWN_SEC = 13020;
const SWAY_DURATION = 0.45;  // seconds a tree wobbles after a (non-killing) hit
const FALL_DURATION = 1.35;  // seconds for a felled tree to tip over then sink/fade into the ground

// Spatial-grid cell size (metres). The per-frame collider + proximity queries only ever look at the
// handful of cells overlapping a small radius around the player, so their cost is independent of how
// many nodes exist in total -- this is what lets the tree/ore count (and the map) grow without the old
// O(all-nodes) scan every frame. A node spans at most a couple of cells, so ~48m keeps buckets tiny.
const CELL = 48;
const GRID_OFF = 4096; // cell-index bias so negative coords pack into a non-negative key (|x| < ~196k m)
const GRID_STRIDE = 8192;

// Forest-density (woodland pockets) now lives in engine/biome.ts so trees, bushes, grass-darkening and
// the biome table all read one field. The value is byte-identical to the old local copy here.
function smoothstep01(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

export class NodeField {
  readonly group = new THREE.Group();
  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly byMesh = new Map<THREE.InstancedMesh, NodeInst[]>();
  // The currently-DRAWN nodes per mesh, indexed by draw slot (raycast resolves instanceId through this).
  // Ore: a static list (slot == index, count never shrinks). Trees: rebuilt each stream pass; a trunk and
  // its foliage mesh share the SAME array (same node at the same slot in both).
  private readonly visibleByMesh = new Map<THREE.InstancedMesh, NodeInst[]>();
  private readonly grid = new Map<number, NodeInst[]>(); // spatial index: packed cell key -> nodes in it
  private streamCellX = NaN; private streamCellZ = NaN; // last stream-cell the camera was in (rebuild on change)
  private streamCamX = 0; private streamCamZ = 0;        // camera pos of the last stream rebuild
  private readonly tmp = new THREE.Matrix4();
  private rng = mulberry32(NODE_SEED);
  private sulfurClusters: Array<[number, number]> = []; // cached rare cluster centres for sulfur
  private readonly treeNodes: NodeInst[] = []; // every tree slot (for recycling into regrowth seeds)
  private readonly treeMeshes: THREE.InstancedMesh[] = []; // trunk + foliage meshes (flag on growth)
  // Far-LOD tree impostors: shared uniforms + material. The vertex shader scales each instance in by camera
  // distance (0 inside impostorNear .. full by impostorFar), so a single always-drawn instanced mesh per
  // species costs ~nothing up close (degenerate) and renders the cheap low-poly tree at range.
  private readonly uImpNear = IMP_FADE.near;
  private readonly uImpFar = IMP_FADE.far;
  private readonly uImpDay = IMP_FADE.day; // 0 night .. 1 day -- dims the unlit billboards like the lit 3D trees
  private readonly uImpCtr = IMP_FADE.ctr; // focus (player) XZ, updated each frame -> drives the LOD cross-fade
  private readonly uImpTint = IMP_FADE.tint; // per-frame sun+ambient colour so billboards match the lit 3D trees
  private readonly impostorMeshes: Array<{ mesh: THREE.InstancedMesh; nodes: NodeInst[] }> = [];
  private readonly growing: NodeInst[] = []; // seeded saplings currently growing toward fullScale
  private readonly animating: NodeInst[] = []; // trees currently swaying (hit) or falling (dead)
  private readonly felledNodes: NodeInst[] = []; // MP: nodes felled via a server op, awaiting their shared-clock respawn
  // Fired ONCE when a node is fully felled (tree chopped / rock mined out). MP wires this to a persisted felledAt op so
  // the fell survives relog + reaches late joiners (server-authoritative world, MULTIPLAYER.md). Guarded remote-side.
  onFelled: (x: number, z: number) => void = () => {};
  // Gate a felled node's respawn: return false to HOLD it (e.g. a base was built over the spot). Wired to the builder
  // so trees never regrow up through someone's foundation -- the node keeps re-checking until the build is gone.
  canRespawnAt: (x: number, z: number) => boolean = () => true;
  private lastT = -1; // last update() time (seconds) to derive dt for the animations
  private lastGrowthDay = -1; // last game day the natural-growth pass ran (once per day)
  private readonly glow: THREE.Sprite;
  private glowNode: NodeInst | null = null;
  private readonly spotWorld = new THREE.Vector3();

  constructor() {
    this.addTrees(NODES.treeCount, NODES.treeHits);
    this.addOre(0xc4c8cc, 'stone', NODES.rockCount, NODES.rockHits, 6); // lighter/whiter than the grey rock formations so mineable stone stands out
    this.addOre(0xb0884f, 'metal_ore', NODES.metalCount, NODES.metalHits, 14); // ore, not the smelted item -- see consumables.smeltResult
    this.addOre(0xd9c34a, 'sulfur', NODES.sulfurCount, NODES.sulfurHits, 14);
    this.rebuildGrid(); // index every node into the spatial grid for cheap nearby queries
    this.glow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: GLOW_TEX, color: 0xffd27a, transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
    }));
    this.glow.visible = false;
    this.glow.renderOrder = 5;
    this.group.add(this.glow);
  }

  // Advance the canopy wind sway and grow any seeded saplings toward their mature size. `day` is the
  // fractional in-game day count (from the day-night cycle); a seed planted on plantDay reaches full
  // over NODES.regrowDays.
  update(time: number, day: number, camX: number, camZ: number): void {
    LEAF_WIND.value = time;
    // Day-boundary hook (was natural forest spread; seedlings are OFF now, so naturalGrowth is a stub -- kept wired
    // so re-enabling is one method). Trees respawn same-spot via the felled timer instead.
    const gDay = Math.floor(day);
    if (this.lastGrowthDay < 0) this.lastGrowthDay = gDay;
    else if (gDay > this.lastGrowthDay) { this.lastGrowthDay = gDay; this.naturalGrowth(gDay); }
    this.uImpCtr.value.set(camX, camZ); // drive the billboard<->3D LOD cross-fade off the player/focus XZ
    // Vegetation streamer: re-upload only the trees within drawRadius of the camera when it crosses a stream
    // cell (so the GPU draws nearby trees, not all of them). Collision/proximity already run off the grid.
    const cellX = Math.floor(camX / NODES.streamCell), cellZ = Math.floor(camZ / NODES.streamCell);
    if (cellX !== this.streamCellX || cellZ !== this.streamCellZ) {
      this.streamCellX = cellX; this.streamCellZ = cellZ;
      this.streamRebuild(camX, camZ);
    }
    const dt = this.lastT < 0 ? 0 : Math.min(0.1, Math.max(0, time - this.lastT));
    this.lastT = time;
    this.animateTrees(dt);
    // MP: respawn server-felled trees/rocks when their shared-clock deadline passes -- every client revives the exact
    // same node at the same wall-clock moment (Date.now() + the server's remaining secs), so the world stays identical.
    if (this.felledNodes.length > 0) {
      const nowS = Date.now() / 1000;
      for (let i = this.felledNodes.length - 1; i >= 0; i--) {
        const node = this.felledNodes[i];
        if (node.reviveAt === undefined || nowS < node.reviveAt) continue;
        if (!this.canRespawnAt(node.pos.x, node.pos.z)) { node.reviveAt = nowS + 30; continue; } // a base is over this spot: hold, re-check in 30s (no tree through a house)
        node.reviveAt = undefined;
        node.alive = true; node.remaining = node.maxHits; node.growing = false; node.dirty = true;
        this.writeImpostor(node, false); // re-show its far-LOD
        this.gridMove(node);
        if (node.slot >= 0) { this.writeMatrixAt(node, node.slot, this.drawFactor(node)); node.mesh.instanceMatrix.needsUpdate = true; if (node.foliage) node.foliage.instanceMatrix.needsUpdate = true; }
        else this.ensureSlot(node); // respawned near the camera with no slot -> draw it now, don't wait for the next stream pass
        this.felledNodes.splice(i, 1);
      }
    }
    if (this.growing.length === 0) return;
    const seed = NODES.regrowSeedFrac;
    const touched = new Set<THREE.InstancedMesh>();
    for (let i = this.growing.length - 1; i >= 0; i--) {
      const node = this.growing[i];
      const t = Math.min(1, Math.max(0, (day - node.plantDay) / NODES.regrowDays));
      node.scale = node.fullScale * (seed + (1 - seed) * t);
      this.writeMatrix(node, 1);
      // Stage the sapling: a young seedling is a BARE twig (foliage hidden) for the first ~55% of growth, then
      // it leafs out toward the full tree -- a recognizable seedling -> young -> mature progression, no new model.
      if (node.foliage && node.slot >= 0 && t < 0.55) { node.foliage.setMatrixAt(node.slot, ZERO_SCALE_M); }
      touched.add(node.mesh);
      if (node.foliage) touched.add(node.foliage);
      if (node.apples) touched.add(node.apples);
      // Fully grown -> a normal tree. Refresh the matrices once so a matured apple tree shows its fruit immediately
      // (even if the player never moves to trigger a stream rebuild).
      if (t >= 1) { node.growing = false; this.growing.splice(i, 1); if (node.slot >= 0) this.writeMatrixAt(node, node.slot, this.drawFactor(node)); }
    }
    for (const m of touched) m.instanceMatrix.needsUpdate = true;
  }

  // Animate trees that were just hit (a brief wobble) or felled (tip over, then sink + fade into the ground).
  // Only writes the matrix for trees that are currently uploaded (slot >= 0); culled ones just tick down.
  private animateTrees(dt: number): void {
    if (this.animating.length === 0 || dt <= 0) return;
    const touched = new Set<THREE.InstancedMesh>();
    for (let i = this.animating.length - 1; i >= 0; i--) {
      const node = this.animating[i];
      if (node.falling) {
        node.fallT = (node.fallT ?? 0) + dt;
        const t = node.fallT / FALL_DURATION;
        if (t >= 1) { // done: stop drawing it (drawFactor returns 0 once falling clears)
          node.falling = false; node.fallT = -1;
          this.writeMatrixAt(node, node.slot, 0);
          touched.add(node.mesh); if (node.foliage) touched.add(node.foliage); if (node.apples) touched.add(node.apples);
          this.animating.splice(i, 1);
          continue;
        }
        const tilt = smoothstep01(0, 0.45, t) * 1.5;          // tip ~86deg over the first 45%
        const sink = Math.max(0, (t - 0.5) / 0.5);            // then sink + fade in the back half
        this.writeAnimated(node, tilt, -sink * 7 * node.scale, Math.max(0.02, 1 - sink));
        touched.add(node.mesh); if (node.foliage) touched.add(node.foliage); if (node.apples) touched.add(node.apples);
      } else if ((node.swayT ?? 0) > 0) {
        node.swayT = (node.swayT ?? 0) - dt;
        if (node.swayT <= 0) { node.swayT = 0; this.writeMatrixAt(node, node.slot, this.drawFactor(node)); this.animating.splice(i, 1); }
        else this.writeAnimated(node, 0.12 * (node.swayT / SWAY_DURATION) * Math.sin(node.swayT * 30), 0, 1); // decaying wobble
        touched.add(node.mesh); if (node.foliage) touched.add(node.foliage); if (node.apples) touched.add(node.apples);
      } else {
        this.animating.splice(i, 1);
      }
    }
    for (const m of touched) m.instanceMatrix.needsUpdate = true;
  }

  // Write a tilted (and optionally sunk + shrunk) tree matrix at its current draw slot. The tilt is about a
  // horizontal axis through the trunk base (the geometry roots at y=0), so the tree leans/falls from the base.
  private writeAnimated(node: NodeInst, tiltAngle: number, sinkY: number, scaleFactor: number): void {
    if (node.slot < 0) return; // streamed out -> nothing to draw this frame
    const dir = node.fallDir ?? 0;
    TMP_A.set(Math.cos(dir), 0, Math.sin(dir));
    TMP_Q.setFromAxisAngle(TMP_A, tiltAngle).multiply(node.quat);
    TMP_B.copy(node.pos); TMP_B.y += sinkY;
    const s = node.scale * scaleFactor;
    this.tmp.compose(TMP_B, TMP_Q, TMP_SCALE.set(s, s, s));
    node.mesh.setMatrixAt(node.slot, this.tmp);
    if (node.foliage) node.foliage.setMatrixAt(node.slot, this.tmp);
    // Apples ride the sway wobble, but drop off the instant the tree starts to fall (the batch scatters to the ground).
    if (node.apples) node.apples.setMatrixAt(node.slot, (node.hasApples && !node.falling) ? this.tmp : ZERO_SCALE_M);
  }

  // Deterministic per-node RNG, keyed on the node's WORLD POSITION (quantized). In multiplayer a felled
  // tree's regrowth (seed count, spots, sizes) and its fall direction MUST match on every client, but the
  // shared this.rng drifts per client -- local swings / sweet-spot relocations advance it a different number
  // of times on each machine, so seeds would scatter to different places and the forests would diverge. A
  // tree's position is identical on all clients (the layout is deterministic, and regrowth seeds are placed
  // by this same synced routine), so seeding off it gives every client the same sequence for the same fell.
  private nodeRng(node: NodeInst): () => number {
    const sx = Math.round(node.pos.x * 4) | 0, sz = Math.round(node.pos.z * 4) | 0;
    return mulberry32((NODE_SEED ^ Math.imul(sx, 2654435761) ^ Math.imul(sz, 40503)) >>> 0);
  }

  private startSway(node: NodeInst): void {
    if (node.falling) return;
    if (node.fallDir === undefined) node.fallDir = this.nodeRng(node)() * Math.PI * 2;
    node.swayT = SWAY_DURATION;
    if (!this.animating.includes(node)) this.animating.push(node);
  }

  private startFall(node: NodeInst): void {
    node.falling = true; node.fallT = 0; node.swayT = 0;
    if (node.fallDir === undefined) node.fallDir = this.nodeRng(node)() * Math.PI * 2;
    if (!this.animating.includes(node)) this.animating.push(node);
  }

  // --- Spatial grid (the Phase-G scaling fix) ---------------------------------------------------
  // Every node lives in exactly one grid bucket keyed by its world cell. The per-frame collider and
  // proximity queries walk only the buckets overlapping a small radius, so their cost is set by how
  // many nodes are NEARBY, not by the total -- which is what lets the count and the map grow.

  private cellKeyAt(x: number, z: number): number {
    return (Math.floor(x / CELL) + GRID_OFF) * GRID_STRIDE + (Math.floor(z / CELL) + GRID_OFF);
  }

  private gridInsert(node: NodeInst): void {
    const key = this.cellKeyAt(node.pos.x, node.pos.z);
    let bucket = this.grid.get(key);
    if (!bucket) { bucket = []; this.grid.set(key, bucket); }
    bucket.push(node);
    node.cell = key;
  }

  // Re-home a node after it moved (a recycled regrowth seed); a no-op while it stays in the same cell.
  private gridMove(node: NodeInst): void {
    const key = this.cellKeyAt(node.pos.x, node.pos.z);
    if (key === node.cell) return;
    if (node.cell >= 0) {
      const old = this.grid.get(node.cell);
      if (old) { const i = old.indexOf(node); if (i >= 0) old.splice(i, 1); }
    }
    this.gridInsert(node);
  }

  // Drop and re-index every node (after the whole layout is re-scattered). Trees register one
  // NodeInst[] under both trunk and foliage meshes, so dedupe to index each node exactly once.
  private rebuildGrid(): void {
    this.grid.clear();
    const seen = new Set<NodeInst[]>();
    for (const nodes of this.byMesh.values()) {
      if (seen.has(nodes)) continue;
      seen.add(nodes);
      for (const node of nodes) this.gridInsert(node);
    }
  }

  // --- Vegetation streamer ----------------------------------------------------------------------
  // Re-upload only the TREES within drawRadius of the camera into compacted draw slots, so the GPU draws
  // the nearby trees instead of all of them. Ore is left always-drawn (cheap). Runs on a stream-cell cross.

  private drawFactor(node: NodeInst): number {
    if (node.falling) return 1; // the fall/fade death animation owns the matrix
    if (node.remaining <= 0) return 0;
    if (node.growing) return 1; // growth loop owns the sapling's scale
    if (node.spotKind === 'tree') return 1; // trees do NOT shrink when chopped (they sway, then fall)
    return 0.5 + 0.5 * Math.max(0, node.remaining) / node.maxHits; // ore still shrinks as it depletes
  }

  private writeMatrixAt(node: NodeInst, slot: number, factor: number): void {
    const s = node.scale * factor;
    this.tmp.compose(node.pos, node.quat, TMP_SCALE.set(s, s, s));
    node.mesh.setMatrixAt(slot, this.tmp);
    if (node.foliage) node.foliage.setMatrixAt(slot, this.tmp);
    // Apple clusters share the foliage slot + matrix, but only for apple-bearing, mature, standing, drawn trees
    // (every other tree of the type zero-scales its apple instance so the canopy stays bare).
    if (node.apples) node.apples.setMatrixAt(slot, (node.hasApples && !node.growing && !node.falling && factor > 0) ? this.tmp : ZERO_SCALE_M);
  }

  private streamRebuild(camX: number, camZ: number): void {
    this.streamCamX = camX; this.streamCamZ = camZ;
    const R = NODES.drawRadius, r2 = R * R;
    // 1) clear the previous tree visibility (O(previously visible)). Trunk + foliage share one array.
    const cleared = new Set<NodeInst[]>();
    for (const m of this.treeMeshes) {
      const vis = this.visibleByMesh.get(m);
      if (!vis || cleared.has(vis)) continue;
      cleared.add(vis);
      for (const n of vis) n.slot = -1;
      vis.length = 0;
    }
    // 2) walk the grid cells overlapping the draw disc; compact alive trees into slots [0..k).
    const cx0 = Math.floor((camX - R) / CELL), cx1 = Math.floor((camX + R) / CELL);
    const cz0 = Math.floor((camZ - R) / CELL), cz1 = Math.floor((camZ + R) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + GRID_OFF) * GRID_STRIDE + (cz + GRID_OFF));
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) {
          const node = bucket[i];
          if (node.spotKind !== 'tree' || (!node.alive && !node.falling)) continue; // ore not streamed; keep falling trees drawn
          const dx = node.pos.x - camX, dz = node.pos.z - camZ;
          if (dx * dx + dz * dz > r2) continue;
          const vis = this.visibleByMesh.get(node.mesh);
          if (!vis || vis.length >= node.mesh.instanceMatrix.count) continue; // capacity guard
          const slot = vis.length;
          node.slot = slot; vis.push(node);
          this.writeMatrixAt(node, slot, this.drawFactor(node));
          if (node.trunkColor) node.mesh.setColorAt(slot, node.trunkColor);
          if (node.foliage && node.foliageColor) node.foliage.setColorAt(slot, node.foliageColor);
        }
      }
    }
    // 3) commit counts + dirty flags. Trunk and foliage share a visible array, so both get the same count.
    for (const m of this.treeMeshes) {
      m.count = this.visibleByMesh.get(m)?.length ?? 0;
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
      m.computeBoundingSphere();
    }
  }

  // PER-FRAME FRUSTUM CULL of streamed trees. The tree meshes are ONE instanced mesh spanning the whole draw disc,
  // so three.js cannot per-instance frustum-cull them -- everything behind you / off to the sides still draws every
  // frame. This hides (matrix scale 0) trees outside the camera frustum and restores them when they swing back in.
  // apply=false only COUNTS the off-screen trees (for the perf probe) and restores anything a prior apply hid.
  // Cheap: a few hundred streamed trees, and a matrix is only re-written the frame a tree crosses the frustum edge.
  // NOTE (first pass): a culled tree also stops casting into the sun shadow map (same instance matrix). The shadow
  // only re-bakes on movement, so rotating is safe, but a shadow cast from just off-screen can pop when you move --
  // that's why this is an opt-in toggle (O). Falling/swaying trees are never culled (their animation owns the matrix).
  cullStreamedTrees(cam: THREE.Camera, apply: boolean): { offscreen: number; culled: number; drawn: number } {
    // Build the frustum from the CURRENT camera pose. matrixWorldInverse is only refreshed by the renderer, so
    // invert the up-to-date world matrix ourselves (this runs before the render) to avoid a one-frame lag.
    cam.updateMatrixWorld();
    _cullMat.copy(cam.matrixWorld).invert().premultiply(cam.projectionMatrix);
    _cullFrustum.setFromProjectionMatrix(_cullMat);
    let offscreen = 0, culled = 0, drawn = 0;
    const seen = new Set<NodeInst[]>();
    const dirty = new Set<THREE.InstancedMesh>();
    for (const m of this.treeMeshes) {
      const vis = this.visibleByMesh.get(m);
      if (!vis || seen.has(vis)) continue; // trunk + foliage share one vis array; walk it once
      seen.add(vis);
      for (const node of vis) {
        if (node.slot < 0) continue;
        const animating = node.falling || (node.swayT ?? 0) > 0;
        // Generous bounding sphere around the whole tree so a partly-visible edge tree is never clipped
        // early, PLUS a ~22m shadow-safety margin: a culled tree stops casting into the sun shadow map,
        // so trees just past the frustum edge stay drawn and their shadows can't pop as you turn/move.
        _cullSphere.center.set(node.pos.x, node.pos.y + 3 * node.scale, node.pos.z);
        _cullSphere.radius = 4 * node.scale + 24;
        const off = !animating && !_cullFrustum.intersectsSphere(_cullSphere);
        if (off) offscreen++;
        const want = apply && off; // desired culled state this frame
        if (want !== !!node.culled) {
          node.culled = want;
          this.writeMatrixAt(node, node.slot, want ? 0 : this.drawFactor(node));
          dirty.add(node.mesh);
          if (node.foliage) dirty.add(node.foliage);
          if (node.apples) dirty.add(node.apples);
        }
        if (node.culled) culled++; else drawn++;
      }
    }
    for (const m of dirty) m.instanceMatrix.needsUpdate = true;
    return { offscreen, culled, drawn };
  }

  // Give a node a draw slot RIGHT NOW if it is near the camera (a freshly planted seed), else leave it for
  // the next stream pass to pick up.
  private ensureSlot(node: NodeInst): void {
    if (node.slot >= 0) return;
    const dx = node.pos.x - this.streamCamX, dz = node.pos.z - this.streamCamZ;
    if (dx * dx + dz * dz > NODES.drawRadius * NODES.drawRadius) return;
    const vis = this.visibleByMesh.get(node.mesh);
    if (!vis || vis.length >= node.mesh.instanceMatrix.count) return;
    const slot = vis.length;
    node.slot = slot; vis.push(node);
    this.writeMatrixAt(node, slot, this.drawFactor(node));
    if (node.trunkColor) node.mesh.setColorAt(slot, node.trunkColor);
    if (node.foliage && node.foliageColor) node.foliage.setColorAt(slot, node.foliageColor);
    const k = slot + 1;
    node.mesh.count = k; node.mesh.instanceMatrix.needsUpdate = true;
    if (node.mesh.instanceColor) node.mesh.instanceColor.needsUpdate = true;
    node.mesh.computeBoundingSphere();
    if (node.foliage) {
      node.foliage.count = k; node.foliage.instanceMatrix.needsUpdate = true;
      if (node.foliage.instanceColor) node.foliage.instanceColor.needsUpdate = true;
      node.foliage.computeBoundingSphere();
    }
    if (node.apples) {
      node.apples.count = k; node.apples.instanceMatrix.needsUpdate = true;
      node.apples.computeBoundingSphere();
    }
  }

  // (removed) plantSeeds: felling used to recycle dead slots into RELOCATED saplings. That relocation is
  // fundamentally incompatible with the server's position-keyed world model -- the server tracks "felled at (x,z)"
  // and cannot follow a sapling that moved to (x2,z2). A felled tree now respawns the SAME tree at the SAME spot on a
  // clock shared by every client (harvest() + markFelledAt(), MULTIPLAYER.md). Seedlings are disabled (owner request).

  // NATURAL FOREST GROWTH: once per game DAY, living mature trees may each seed one sapling nearby (recycling a
  // dead tree slot), so forests slowly expand on their own over time. The chance + spot are seeded off the tree
  // position + the day, so every MP client agrees on WHICH trees seed WHERE; the recycled slot may differ by
  // chop history (fails soft -- a naturally grown tree just may not exist on a client that never chopped). Bounded
  // by the number of free dead slots, so it can never overgrow the fixed tree buffer.
  private naturalGrowth(_day: number): void {
    // SEEDLINGS DISABLED (owner: Rust-like -- trees respawn same-spot on the felled timer only, no saplings). Natural
    // sapling spread is off because those saplings were dynamic entities NOT in the server's position-keyed model, so
    // late joiners would never see them (a desync). Kept as a stub so re-enabling is a one-method change if we later
    // make spread server-persistent. The old recycle-into-saplings body was removed.
  }

  // Regenerate the whole node layout for the CURRENT terrain (after the admin panel reshapes the
  // land). Each node is re-scattered onto valid land (scatter retries until it is above the waterline,
  // so trees + ore never end up in the sea), re-seated on the new ground, and reset to full health.
  // Reproducible: the rng is reseeded so the same terrain yields the same layout. Trees register one
  // NodeInst[] under two meshes, so dedupe to avoid scattering each tree twice.
  regenerate(): void {
    this.rng = mulberry32(NODE_SEED);
    this.sulfurClusters = []; // rebuild rare sulfur clusters for the new terrain
    this.growing.length = 0; // drop any in-progress saplings; the forest is laid out fresh
    this.animating.length = 0; // drop any swaying/falling animations
    const seen = new Set<NodeInst[]>();
    for (const nodes of this.byMesh.values()) {
      if (seen.has(nodes)) continue;
      seen.add(nodes);
      for (const node of nodes) {
        node.scale = node.fullScale; // restore mature size (in case it was a growing sapling)
        node.growing = false;
        node.dirty = false; // fresh layout: nothing diverges any more
        node.falling = false; node.fallT = -1; node.swayT = 0;
        const [x, z] = node.spotKind === 'tree' ? this.scatterTree(node.inner) : this.oreScatter(node.item, node.inner);
        const base = terrainHeight(x, z);
        node.pos.set(x, node.spotKind === 'tree' ? base : base + node.scale * 0.5, z);
        if (node.apples) node.hasApples = hashApple(x, z); // re-roll apple-ness to match the tree's new spot
        node.alive = true;
        node.remaining = node.maxHits;
        if (node.spotKind === 'tree') node.slot = -1; // trees are re-uploaded by the stream pass below
        else this.writeMatrix(node, 1); // ore is always drawn at its fixed slot
      }
    }
    for (const mesh of this.meshes) mesh.instanceMatrix.needsUpdate = true;
    // Re-seat the far-LOD impostors at the trees' new scattered positions (all alive after a regenerate).
    for (const { mesh, nodes } of this.impostorMeshes) {
      for (const node of nodes) {
        this.tmp.compose(node.pos, node.quat, TMP_SCALE.set(node.fullScale, node.fullScale, node.fullScale));
        mesh.setMatrixAt(node.index, this.tmp);
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
    this.rebuildGrid(); // every node moved, so re-index the whole spatial grid
    this.streamRebuild(this.streamCamX, this.streamCamZ); // re-stream trees around the player for the new layout
    this.streamCellX = Math.floor(this.streamCamX / NODES.streamCell);
    this.streamCellZ = Math.floor(this.streamCamZ / NODES.streamCell);
  }

  // World save: only the nodes that diverged from the deterministic layout. Trees ride their dirty
  // flag (it also captures recycled seeds, which MOVE); ore has no flag because remaining < maxHits
  // is the complete story (ore never moves and never regrows).
  serialize(): SavedNodes {
    const r2 = (v: number): number => Math.round(v * 100) / 100;
    const trees: SavedTree[] = [];
    for (let i = 0; i < this.treeNodes.length; i++) {
      const n = this.treeNodes[i];
      if (!n.dirty) continue;
      const t: SavedTree = { i, x: r2(n.pos.x), y: r2(n.pos.y), z: r2(n.pos.z), s: Math.round(n.fullScale * 1000) / 1000, rem: n.remaining };
      if (n.growing) { t.g = 1; t.d = Math.round(n.plantDay * 1000) / 1000; }
      trees.push(t);
    }
    const ore: SavedOre[] = [];
    const seen = new Set<NodeInst[]>();
    for (const nodes of this.byMesh.values()) {
      if (seen.has(nodes)) continue;
      seen.add(nodes);
      for (const n of nodes) if (n.spotKind === 'boulder' && n.remaining < n.maxHits) ore.push({ k: oreKey(n.item, n.pos.x, n.pos.z), rem: n.remaining });
    }
    return { trees, ore };
  }

  // Re-apply on boot, before the first frame: the tree stream has not run yet (streamCellX is NaN), so
  // fixing alive/pos/scale here is enough for the first stream pass to draw the right forest. Ore is
  // always-drawn at fixed slots, so its shrunk/hidden matrices are rewritten immediately. plantDay is
  // absolute against the persisted day clock (restored in the same apply()), so growth just continues.
  deserialize(data: SavedNodes): void {
    for (const t of data.trees) {
      const node = this.treeNodes[t.i];
      if (!node) continue; // layout shrank (config change): drop the orphan, that tree regrows fresh
      node.pos.set(t.x, t.y, t.z);
      if (node.apples) node.hasApples = hashApple(t.x, t.z); // keep fruit consistent with a restored (possibly moved) tree
      node.fullScale = t.s;
      node.remaining = Math.min(t.rem, node.maxHits);
      node.alive = node.remaining > 0;
      node.growing = !!t.g && node.alive;
      node.plantDay = t.d ?? 0;
      node.falling = false; node.fallT = -1; node.swayT = 0; // mid-fall at save time = simply gone now
      node.scale = node.growing ? node.fullScale * NODES.regrowSeedFrac : node.fullScale; // growth loop re-derives per frame
      node.dirty = true; // still a delta: keep carrying it in every future save
      this.writeImpostor(node, !node.alive); // hide the chopped far-LOD / re-seat a moved one
      this.gridMove(node); // recycled seeds can sit cells away from the slot's original bucket
      if (node.growing) this.growing.push(node);
    }
    if (data.ore.length > 0) {
      const byKey = new Map<string, number>();
      for (const o of data.ore) byKey.set(o.k, o.rem);
      const touched = new Set<THREE.InstancedMesh>();
      const seen = new Set<NodeInst[]>();
      for (const nodes of this.byMesh.values()) {
        if (seen.has(nodes)) continue;
        seen.add(nodes);
        for (const n of nodes) {
          if (n.spotKind !== 'boulder') continue;
          const rem = byKey.get(oreKey(n.item, n.pos.x, n.pos.z));
          if (rem === undefined) continue;
          n.remaining = Math.min(Math.max(0, rem), n.maxHits);
          n.alive = n.remaining > 0;
          this.writeMatrix(n, n.alive ? this.drawFactor(n) : 0);
          touched.add(n.mesh);
        }
      }
      for (const m of touched) { m.instanceMatrix.needsUpdate = true; m.computeBoundingSphere(); }
    }
  }

  // Nearest living node under the ray plus the world-space hit point, or null.
  raycastHit(raycaster: THREE.Raycaster): { node: NodeInst; point: THREE.Vector3 } | null {
    const hits = raycaster.intersectObjects(this.meshes, false);
    for (const hit of hits) {
      if (hit.instanceId == null) continue;
      // instanceId is the current DRAW slot, so resolve through visibleByMesh (trees stream; ore is static).
      const node = this.visibleByMesh.get(hit.object as THREE.InstancedMesh)?.[hit.instanceId];
      if (node && node.alive) return { node, point: hit.point };
    }
    return null;
  }

  // Cheap proximity test: is any LIVING node within `range` of (px,pz)? Lets the caller skip the
  // costly instanced raycast (raycastHit) entirely when the player is nowhere near a harvestable -- the
  // harvest glow only ever shows within gatherRange, so when nothing is in reach there is nothing to find.
  anyAliveWithin(px: number, pz: number, range: number): boolean {
    const r2 = range * range;
    const cx0 = Math.floor((px - range) / CELL), cx1 = Math.floor((px + range) / CELL);
    const cz0 = Math.floor((pz - range) / CELL), cz1 = Math.floor((pz + range) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + GRID_OFF) * GRID_STRIDE + (cz + GRID_OFF));
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) {
          const node = bucket[i];
          if (!node.alive || node.growing) continue; // growing saplings are NOT harvestable yet
          const dx = node.pos.x - px, dz = node.pos.z - pz;
          if (dx * dx + dz * dz <= r2) return true;
        }
      }
    }
    return false;
  }

  // Forgiving harvest target: the NEAREST living node within `range` of (px,pz), regardless of precise aim.
  // Used as the click/glow fallback when the exact raycast misses, so a tree/rock you stand next to is easy
  // to hit. Returns the node + its base position as the (approximate) hit point.
  private nodeIdSeq = 1;
  private readonly idMap = new Map<number, NodeInst>();

  // Stable id for a node, assigned + registered the first time it is targeted (so a gather can be sent
  // over the wire). Lazy, so node creation paths stay untouched (spec section 19).
  idOf(node: NodeInst): number {
    if (node.id === undefined) { node.id = this.nodeIdSeq++; this.idMap.set(node.id, node); }
    return node.id;
  }

  nodeById(id: number): NodeInst | null { return this.idMap.get(id) ?? null; }

  nearestAlive(px: number, pz: number, range: number): { node: NodeInst; point: THREE.Vector3 } | null {
    let best: NodeInst | null = null, bestD = range * range;
    const cx0 = Math.floor((px - range) / CELL), cx1 = Math.floor((px + range) / CELL);
    const cz0 = Math.floor((pz - range) / CELL), cz1 = Math.floor((pz + range) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + GRID_OFF) * GRID_STRIDE + (cz + GRID_OFF));
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) {
          const node = bucket[i];
          if (!node.alive || node.growing) continue; // growing saplings are NOT harvestable yet (chop the mature trees)
          const dx = node.pos.x - px, dz = node.pos.z - pz, dd = dx * dx + dz * dz;
          if (dd < bestD) { bestD = dd; best = node; }
        }
      }
    }
    return best ? { node: best, point: best.pos } : null;
  }

  // SERVER-AUTHORITATIVE fell (MP welcome + relog): mark the node nearest (x,z) as felled and queue its shared-clock
  // respawn from the server-supplied remaining seconds, so a tree/rock someone chopped stays down for a late joiner and
  // comes back at the same moment on every client. Mirrors the depleted branch of harvest() minus loot/animation.
  markFelledAt(x: number, z: number, remainingSec: number): void {
    const hit = this.nearestAlive(x, z, 0.9); // tight: distinct nodes are far apart, so this resolves the exact one
    if (!hit) return;
    const node = hit.node;
    node.remaining = 0;
    node.alive = false;
    node.dirty = true;
    node.reviveAt = Date.now() / 1000 + Math.max(1, remainingSec); // wall-clock -> identical respawn moment for everyone
    if (node.spotKind === 'boulder') {
      this.writeMatrix(node, 0);
      node.mesh.instanceMatrix.needsUpdate = true;
    } else {
      this.writeImpostor(node, true); // hide the far-LOD billboard
      if (node.slot >= 0) { // if it is currently drawn near the camera, hide the instance now (no fall animation on a bulk apply)
        this.writeMatrixAt(node, node.slot, 0);
        node.mesh.instanceMatrix.needsUpdate = true;
        if (node.foliage) node.foliage.instanceMatrix.needsUpdate = true;
        if (node.apples) node.apples.instanceMatrix.needsUpdate = true;
      }
    }
    this.felledNodes.push(node);
  }

  // Up to `max` nearby LEAFY tree canopies for the falling-leaf shed: must be a live, mature tree that actually
  // HAS a foliage mesh (so bare DEAD trees and cacti -- which have no foliage -- are skipped) and is NOT a palm
  // (fronds do not shed leaves). Grid-scanned around (px,pz), returns a canopy point per tree.
  leafyTreesNear(px: number, pz: number, range: number, max: number): Array<{ x: number; y: number; z: number; color: number }> {
    const out: Array<{ x: number; y: number; z: number; color: number }> = [];
    const r2 = range * range;
    const cx0 = Math.floor((px - range) / CELL), cx1 = Math.floor((px + range) / CELL);
    const cz0 = Math.floor((pz - range) / CELL), cz1 = Math.floor((pz + range) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + GRID_OFF) * GRID_STRIDE + (cz + GRID_OFF));
        if (!bucket) continue;
        for (const node of bucket) {
          if (out.length >= max) return out;
          if (!node.alive || node.growing || node.spotKind !== 'tree' || !node.foliage || node.isPalm) continue;
          const dx = node.pos.x - px, dz = node.pos.z - pz;
          if (dx * dx + dz * dz > r2) continue;
          out.push({ x: node.pos.x, y: node.pos.y + 2.8 * node.scale, z: node.pos.z, color: this.leafGreen(node.foliageColor) });
        }
      }
    }
    return out;
  }

  // The foliage colour of the tree nearest (x,z) within range (for the chop leaf-burst to match), or a default green.
  leafColorNear(px: number, pz: number, range: number): number {
    const n = this.nearestAlive(px, pz, range);
    return this.leafGreen(n ? n.node.foliageColor : undefined);
  }

  // A tree's stored foliageColor is a PALE tint (lightness ~0.7-0.9) that only looks right MULTIPLIED over the dark
  // leaf TEXTURE on the canopy mesh. A solid, untextured falling leaf using it directly washes out near-white --
  // the same reason palms pick a deeper frond colour. Rebuild it as a real foliage green: keep the tree's HUE
  // (clamped to a leaf-green band so a bluish snow-frost tint can't go cyan), then force a solid leaf saturation +
  // lightness so the shed/chop leaves match the canopy instead of fluttering down as white paper.
  leafGreen(fol?: THREE.Color): number {
    if (!fol) return 0x5d8434;
    const hsl = { h: 0, s: 0, l: 0 };
    fol.getHSL(hsl);
    // Track the CANOPY's own saturation/lightness (owner: shed leaves read too bright vs the tree) --
    // only clamp the hue into the leaf-green band and keep lightness at-or-below the canopy's.
    const h = Math.min(0.34, Math.max(0.20, hsl.h));
    return new THREE.Color().setHSL(h, Math.min(Math.max(hsl.s, 0.35), 0.65), Math.min(Math.max(hsl.l, 0.07), 0.15)).getHex();
  }

  // A nearby FRUIT-bearing tree (an apple oak/aspen or a coconut palm), for the wind-gust fruit drop. Returns the
  // fruit item id + a canopy point to drop from, or null. Grid-scanned so it stays cheap. Returns the first match.
  fruitTreeNear(px: number, pz: number, range: number): { item: ItemId; x: number; y: number; z: number } | null {
    const r2 = range * range;
    const cx0 = Math.floor((px - range) / CELL), cx1 = Math.floor((px + range) / CELL);
    const cz0 = Math.floor((pz - range) / CELL), cz1 = Math.floor((pz + range) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + GRID_OFF) * GRID_STRIDE + (cz + GRID_OFF));
        if (!bucket) continue;
        for (const node of bucket) {
          if (!node.alive || node.growing || node.spotKind !== 'tree') continue;
          const item: ItemId | null = node.hasApples ? 'apple' : node.isPalm ? 'coconut' : null;
          if (!item) continue;
          const dx = node.pos.x - px, dz = node.pos.z - pz;
          if (dx * dx + dz * dz > r2) continue;
          return { item, x: node.pos.x, y: node.pos.y + 3 * node.scale, z: node.pos.z };
        }
      }
    }
    return null;
  }

  // Solid colliders for LIVING nodes within `range` of (px,pz), as oriented boxes the player
  // collision pass consumes. Spatially culled so we only ever feed the handful of nodes nearby.
  // Trees: a slim upright box on the TRUNK only (so you can still walk under the canopy). Rocks:
  // a box at the boulder centre. Vertical placement mirrors how addTrees/addOre seat each node.
  collectColliders(out: Box[], pool: BoxPool, px: number, pz: number, range: number): void {
    const r2 = range * range;
    // Only the grid cells overlapping the query circle -- the spatial index holds each node exactly
    // once (trunk + foliage share one NodeInst), so no dedup is needed and no distant node is touched.
    const cx0 = Math.floor((px - range) / CELL), cx1 = Math.floor((px + range) / CELL);
    const cz0 = Math.floor((pz - range) / CELL), cz1 = Math.floor((pz + range) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + GRID_OFF) * GRID_STRIDE + (cz + GRID_OFF));
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) {
          const node = bucket[i];
          if (!node.alive) continue;
          const dx = node.pos.x - px, dz = node.pos.z - pz;
          if (dx * dx + dz * dz > r2) continue;
          if (node.spotKind === 'tree') {
            // node.pos.y is the trunk base (terrain); stand a slim box up its rough trunk height.
            const hr = 0.18 * node.scale;
            const hy = 1.0 * node.scale;
            out.push(pool.take(node.pos.x, node.pos.y + hy, node.pos.z, hr, hy, hr, 0));
          } else {
            // node.pos is the boulder centre (terrain + s*0.5); box it on all axes.
            const he = 0.55 * node.scale;
            out.push(pool.take(node.pos.x, node.pos.y, node.pos.z, he, he, he, 0));
          }
        }
      }
    }
  }

  // Show the glowing sweet spot on the aimed node (facing the camera), or hide it. Trees glow too now
  // (owner QoL): a moving weak-point on the TRUNK, always within player reach (see computeSpot's clamp).
  showGlow(node: NodeInst | null, camPos: THREE.Vector3): void {
    if (node !== this.glowNode) {
      this.glowNode = node;
      if (node) {
        // If the spot is on the far side when newly targeted, bring it round to face the camera.
        TMP_A.copy(camPos).sub(node.pos); TMP_A.y = 0; TMP_A.normalize();
        if (node.spot.x * TMP_A.x + node.spot.z * TMP_A.z < 0.2) this.relocateSpot(node, camPos);
      }
    }
    if (!node) { this.glow.visible = false; return; }
    this.computeSpot(node);
    this.glow.visible = true;
    this.glow.position.copy(this.spotWorld);
    const s = 0.5 * node.scale + 0.15;
    this.glow.scale.set(s, s, s);
  }

  // Harvest one swing. The node holds a FIXED total (perHit * maxHits); each swing extracts `units` of that
  // total = the baseline (1) doubled on an ore sweet-spot, times the tool multiplier `mult` (a better axe/pickaxe
  // empties the node in fewer swings for the SAME total). Metal nodes also drop a stone byproduct on every swing.
  harvest(node: NodeInst, hitPoint: THREE.Vector3 | null, camPos: THREE.Vector3, mult = 1): HarvestResult & { sweet: boolean } {
    const isOre = node.spotKind === 'boulder';
    // Backstop: a growing sapling can't be chopped for wood -- let it mature first (targeting already skips them).
    if (!isOre && node.growing) return { item: node.item, amount: 0, depleted: false, pos: node.pos.clone(), sweet: false };
    // Sweet-spot bonus (ore AND trees now -- owner QoL): hitting the glow pulls double the units
    // (faster, not more total). The tree spot sits on the trunk at hand height (computeSpot clamps it).
    this.computeSpot(node);
    const sweet = hitPoint != null && hitPoint.distanceTo(this.spotWorld) < (isOre ? 0.6 * node.scale + 0.2 : 0.55);
    // Units this swing = baseline(1) * sweet(2) * tool multiplier, capped at what's left so the total never overshoots.
    const units = Math.min(node.remaining, (sweet ? 2 : 1) * Math.max(1, mult));
    node.remaining -= units;
    node.dirty = true; // any extraction diverges from the pristine layout -> persist this node
    const depleted = node.remaining <= 0;
    if (depleted) { node.alive = false; this.onFelled(node.pos.x, node.pos.z); } // MP: persist the fell (guarded on the remote-apply side)
    if (isOre) {
      this.writeMatrix(node, depleted ? 0 : this.drawFactor(node)); // ore shrinks as it depletes
      node.mesh.instanceMatrix.needsUpdate = true;
      node.mesh.computeBoundingSphere();
      if (depleted) { node.reviveAt = Date.now() / 1000 + NODE_RESPAWN_SEC; this.felledNodes.push(node); } // mined-out rock respawns same-spot on the shared clock
      else this.relocateSpot(node, camPos); // move the sweet spot after every swing
    } else if (depleted) {
      this.startFall(node);     // felled tree: tip over + fade into the ground (cosmetic, local)
      this.writeImpostor(node, true); // hide its far-LOD impostor so it does not phantom when you walk off
      // SERVER-AUTHORITATIVE respawn: same tree, same spot, at a moment shared by every client (replaces the old
      // client-local sapling regrow, which drifted per client and could not stay consistent -- MULTIPLAYER.md).
      node.reviveAt = Date.now() / 1000 + NODE_RESPAWN_SEC;
      this.felledNodes.push(node);
    } else {
      this.startSway(node);     // tree hit (not killed): a quick wobble, NO shrink
      this.relocateSpot(node, camPos); // the trunk weak-point MOVES after every hit (skill-chase, like ore)
    }
    const amount = Math.max(1, Math.round(node.perHit * units));
    const secondary = node.secondaryItem && node.secondaryPerHit
      ? { item: node.secondaryItem, amount: Math.max(1, Math.round(node.secondaryPerHit * units)) }
      : undefined;
    // Felling an apple tree shakes its whole crop loose: a batch of apples the caller scatters at the stump for the group.
    const apples = (!isOre && depleted && node.hasApples) ? NODES.appleTreeYield : 0;
    return { item: node.item, amount, secondary, apples, depleted, pos: node.pos.clone(), sweet };
  }

  private computeSpot(node: NodeInst): void {
    this.spotWorld.copy(node.spot).multiplyScalar(node.scale).add(node.pos);
    // TREES: the weak-point must stay ON THE TRUNK within player reach -- a big tree's scaled spot
    // would ride metres up the trunk otherwise. Clamp the world HEIGHT only (x/z keep the scaled
    // trunk-surface radius).
    if (node.spotKind !== 'boulder') this.spotWorld.y = node.pos.y + Math.min(1.6, Math.max(0.7, node.spot.y));
  }

  // Place the sweet spot on the camera-facing side so it stays visible.
  private relocateSpot(node: NodeInst, camPos: THREE.Vector3): void {
    TMP_A.copy(camPos).sub(node.pos); TMP_A.y = 0;
    if (TMP_A.lengthSq() < 1e-4) TMP_A.set(0, 0, 1);
    TMP_A.normalize();
    const a = this.rng() * Math.PI * 2;
    const cz = 0.4 + this.rng() * 0.55;
    const sz = Math.sqrt(1 - cz * cz);
    TMP_B.set(Math.cos(a) * sz, Math.sin(a) * sz, cz);
    TMP_Q.setFromUnitVectors(FWD, TMP_A);
    TMP_B.applyQuaternion(TMP_Q);
    if (node.spotKind === 'boulder') node.spot.set(TMP_B.x * 0.8, TMP_B.y * 0.5, TMP_B.z * 0.8);
    else node.spot.set(TMP_B.x * 0.42, 1.2 + TMP_B.y * 0.5, TMP_B.z * 0.42);
  }

  private addOre(color: number, item: ItemId, count: number, hits: number, inner: number): void {
    const metal = item === 'metal_ore';
    const oreMat = new THREE.MeshStandardMaterial({
      color,
      vertexColors: true, // baked dark contact base -> lighter crown
      roughness: metal ? 0.5 : 0.92,
      metalness: metal ? 0.45 : 0,
    });
    applyStoneDetail(oreMat, 'ore-' + item); // strata + mineral speckle + crevice AO, like the rock formations
    applyInteriorDark(oreMat, true); // darken/reveal with the base interior cone (shared with the ground)
    const mesh = new THREE.InstancedMesh(makeBoulderGeometry(), oreMat, count);
    mesh.name = item;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const roll = (lo: number, hi: number): number => lo + this.rng() * (hi - lo); // uniform per-node total in [lo,hi]
    const nodes: NodeInst[] = [];
    for (let i = 0; i < count; i++) {
      // Stone biases uphill (hills/mountains), sulfur clumps in a few rare spots, metal scatters evenly.
      const [x, z] = this.oreScatter(item, inner);
      const s = 0.98 + this.rng() * 0.08; // ~player-height (boulder is 1.84m tall at s=1; player 1.77m), near-uniform
      // Upright (Y-only) rotation so the flatter, buried base stays down.
      const quat = new THREE.Quaternion().setFromAxisAngle(UP, this.rng() * Math.PI * 2);
      // Per-node TOTAL yield (Rust-like), divided across `hits` baseline rock-swings -> perHit is per-UNIT.
      const total = item === 'stone' ? roll(NODES.stoneNodeMin, NODES.stoneNodeMax)
        : metal ? roll(NODES.metalNodeMetalMin, NODES.metalNodeMetalMax)
        : roll(NODES.sulfurNodeMin, NODES.sulfurNodeMax);
      const node: NodeInst = {
        mesh, index: i, pos: new THREE.Vector3(x, terrainHeight(x, z) + s * 0.5, z), quat,
        scale: s, remaining: hits, maxHits: hits, perHit: total / hits, alive: true, item,
        // Metal nodes also drop stone (byproduct), granted on every swing alongside the metal.
        secondaryItem: metal ? 'stone' : undefined,
        secondaryPerHit: metal ? roll(NODES.metalNodeStoneMin, NODES.metalNodeStoneMax) / hits : undefined,
        spot: new THREE.Vector3(0, 0.3, 0.75), spotKind: 'boulder', inner,
        fullScale: s, growing: false, plantDay: 0, cell: -1, slot: i, // ore: fixed slot == index, always drawn
      };
      nodes.push(node);
      this.writeMatrix(node, 1);
    }
    mesh.instanceMatrix.needsUpdate = true;
    this.register(mesh, nodes);
  }

  // Trees come from EZ-Tree (real forking branch geometry + leaf cards). Every tree is scattered, then
  // routed to a SPECIES by its biome (BIOMES[b].trees): pine/oak/aspen in temperate forest, snow-pine on
  // mountains/snow, bare dead trees in irradiated/rocky, cactus in desert. Each species becomes one
  // trunk (+ optional foliage) InstancedMesh. The scatter concentrates trees into forest pockets.
  private addTrees(count: number, hits: number): void {
    const sea = TERRAIN.seaLevel;
    // Bucket by species AND geometry variant (cactus/palm ship 3 looks each; a variant is its own
    // InstancedMesh but shares the species' material, billboard and gameplay config).
    const buckets = new Map<string, { sp: TreeSpecies; variant: number; arr: Array<[number, number]> }>();
    for (let i = 0; i < count; i++) {
      const [x, z] = this.scatterTree(8);
      const h = terrainHeight(x, z);
      if (h <= sea + 2.5) continue;
      const list = BIOMES[biomeAt(x, z, h)].trees;
      if (!list.length) continue; // beach/ocean: no trees
      const sp = list[(this.rng() * list.length) | 0];
      const variant = (this.rng() * (TREE_VARIANT_COUNT[sp] ?? 1)) | 0;
      const key = sp + '|' + variant;
      let b = buckets.get(key);
      if (!b) { b = { sp, variant, arr: [] }; buckets.set(key, b); }
      b.arr.push([x, z]);
    }
    for (const b of buckets.values()) this.addTreeType(b.arr, hits, b.sp, b.variant);
  }

  // Build one species' trunk (+ leaf-card foliage, unless bare like dead/cactus) InstancedMesh from a list
  // of world positions. Per-tree bark/foliage tints vary each instance; harvest/regrowth/collision apply.
  private addTreeType(positions: Array<[number, number]>, hits: number, sp: TreeSpecies, variant = 0): void {
    const count = positions.length;
    if (count <= 0) return;
    const parts = treeParts(sp, variant);
    const trunkMesh = new THREE.InstancedMesh(parts.trunk, sp === 'cactus' ? CACTUS_MAT : sp === 'palm' ? PALM_TRUNK_MAT : TRUNK_MAT, count);
    trunkMesh.name = 'wood';
    trunkMesh.castShadow = true;
    trunkMesh.receiveShadow = true;
    let foliageMesh: THREE.InstancedMesh | null = null;
    if (parts.foliage) {
      foliageMesh = new THREE.InstancedMesh(parts.foliage, sp === 'palm' ? PALM_FROND_MAT : LEAF_MAT, count);
      foliageMesh.name = 'wood';
      foliageMesh.castShadow = true;
      if (sp !== 'palm') foliageMesh.customDepthMaterial = LEAF_DEPTH; // alpha-cut leafy shadows; palm fronds are solid
    }
    // APPLE TREES: oak/aspen only bear fruit. One extra instanced mesh of red canopy clusters, sized to THIS
    // species' foliage volume, streamed in lockstep with the foliage (shared slot). Not raycastable -- you chop
    // the trunk, not the apples. Per-tree apple-ness is decided below by a position hash (leaves the layout rng alone).
    let applesMesh: THREE.InstancedMesh | null = null;
    if (parts.foliage && (sp === 'oak' || sp === 'aspen')) {
      parts.foliage.computeBoundingBox();
      applesMesh = new THREE.InstancedMesh(makeAppleClusterGeo(parts.foliage.boundingBox!), APPLE_MAT, count);
      applesMesh.name = 'apples';
      applesMesh.castShadow = false; applesMesh.receiveShadow = false;
      applesMesh.count = 0; // nothing drawn until the stream picks up nearby trees
    }
    const nodes: NodeInst[] = [];
    const col = new THREE.Color();
    for (let i = 0; i < count; i++) {
      const [x, z] = positions[i];
      // Base size variation x a 2.0..3.3 multiplier, so the world has proper big trees. CACTI OPT OUT:
      // they are people-sized plants (owner reference photos), not trees -- no tree multiplier.
      const s = sp === 'cactus'
        ? 0.85 + this.rng() * 0.65
        : (0.8 + this.rng() * 1.0) * (NODES.treeScaleMulLo + this.rng() * (NODES.treeScaleMulHi - NODES.treeScaleMulLo));
      const quat = new THREE.Quaternion().setFromAxisAngle(UP, this.rng() * Math.PI * 2);
      // Wood a tree holds scales with its SIZE: normalize this tree's scale across the possible range, then map
      // (slightly super-linear so the biggest trees feel like a real haul) into [treeWoodMin, treeWoodMax].
      const minScale = 0.8 * NODES.treeScaleMulLo, maxScale = 1.8 * NODES.treeScaleMulHi;
      const t = Math.min(1, Math.max(0, (s - minScale) / (maxScale - minScale)));
      const woodTotal = NODES.treeWoodMin + Math.pow(t, 1.3) * (NODES.treeWoodMax - NODES.treeWoodMin);
      // SEAT on the LOWEST ground under the trunk footprint (a slope drops the downhill edge), so trunks
      // are always rooted INTO the hill -- never hovering off the downhill side.
      let seatY = terrainHeight(x, z);
      const seatR = 0.45 * s;
      for (const [ox, oz] of [[seatR, 0], [-seatR, 0], [0, seatR], [0, -seatR]] as const) seatY = Math.min(seatY, terrainHeight(x + ox, z + oz));
      const node: NodeInst = {
        mesh: trunkMesh, foliage: foliageMesh ?? undefined, apples: applesMesh ?? undefined, index: i,
        hasApples: applesMesh != null && hashApple(x, z), // ~appleTreeChance of apple-species trees, deterministic per position
        isPalm: sp === 'palm', // bears coconuts (all palms) -- for the wind-gust fruit drop
        pos: new THREE.Vector3(x, seatY, z), quat,
        scale: s, remaining: hits, maxHits: hits, perHit: woodTotal / hits, alive: true, item: sp === 'cactus' ? 'cactus_flesh' : 'wood',
        spot: new THREE.Vector3(0, 1.2, 0.4), spotKind: 'tree', inner: 8,
        fullScale: s, growing: false, plantDay: 0, cell: -1, slot: -1, // -1: not drawn until a stream pass picks it up
      };
      nodes.push(node);
      this.treeNodes.push(node);
      // STORE the per-tree tints on the node (re-uploaded each stream pass into the node's current slot) rather
      // than baking them into a fixed slot -- slots are recompacted as the player moves.
      const r = this.rng();
      if (foliageMesh) {
        // snow-pine has a frosted canopy; palm fronds get a deeper, more saturated green (they have no leaf
        // texture to darken them, so the pale canopy tint would wash out white); everything else a varied green.
        if (sp === 'snowpine') col.setRGB(0.92 + r * 0.06, 0.94 + r * 0.05, 0.99); // snow-laden canopy: white with the faintest green ghost underneath
        else if (sp === 'palm') col.setHSL(0.22 + r * 0.11, 0.44 + r * 0.30, 0.30 + r * 0.17); // real spread: yellow-green fronds through deep tropical green
        else col.setHSL(0.20 + r * 0.13, 0.32 + r * 0.22, 0.70 + r * 0.22);
        node.foliageColor = col.clone();
      }
      // Trunk tint (instanceColor multiplies the material): green-grey for cactus, grey driftwood for dead,
      // varied warm browns for the rest.
      if (sp === 'cactus') { const v = 0.82 + r * 0.30; col.setRGB(v, v, v); }
      else if (sp === 'snowpine') { const g = 0.72 + r * 0.16; col.setRGB(g, g * 1.02, g * 1.08); } // frosted pale bark
      else if (sp === 'dead') { const g = 0.55 + r * 0.16; col.setRGB(g, g * 0.9, g * 0.78); }
      else { const bk = 0.85 + r * 0.45; col.setRGB(Math.min(1, bk), Math.min(1, bk * (0.86 + this.rng() * 0.12)), Math.min(1, bk * (0.7 + this.rng() * 0.12))); }
      node.trunkColor = col.clone();
    }
    const vis: NodeInst[] = []; // shared visible list: trunk + foliage stream to the SAME slots
    trunkMesh.count = 0; // nothing drawn until the first stream pass uploads the nearby trees
    this.meshes.push(trunkMesh);
    this.treeMeshes.push(trunkMesh);
    this.byMesh.set(trunkMesh, nodes);
    this.visibleByMesh.set(trunkMesh, vis);
    this.group.add(trunkMesh);
    if (foliageMesh) {
      foliageMesh.count = 0;
      this.meshes.push(foliageMesh);
      this.treeMeshes.push(foliageMesh);
      this.byMesh.set(foliageMesh, nodes);
      this.visibleByMesh.set(foliageMesh, vis); // same array as the trunk -> same node at the same slot
      this.group.add(foliageMesh);
    }
    if (applesMesh) {
      // Apples share the trunk/foliage slot + vis array, so they stream + cull in lockstep. NOT pushed to
      // this.meshes: they must never be raycast harvest targets -- you chop the trunk, the crop drops on the fell.
      this.treeMeshes.push(applesMesh);
      this.byMesh.set(applesMesh, nodes);
      this.visibleByMesh.set(applesMesh, vis);
      this.group.add(applesMesh);
    }
    // Far-LOD BILLBOARD: one always-drawn instanced quad per species, textured from the captured tree LOD
    // image (/lods/trees/<sp>_md.png). The vertex shader makes each quad face the camera (around Y)
    // and scales it in by distance, so it is free up close (degenerate) and the only tree geometry drawn at
    // range -- the real tree, flattened, instead of a procedural blob. Chop/regrow/regenerate keep it synced.
    parts.trunk.computeBoundingBox();
    const bbox = parts.trunk.boundingBox!.clone();
    if (parts.foliage) { parts.foliage.computeBoundingBox(); bbox.union(parts.foliage.boundingBox!); }
    const impMesh = new THREE.InstancedMesh(makeBillboardQuad(bbox.min.y, bbox.max.y), this.makeBillboardMaterial(`/lods/trees/${sp}_md.png`), count);
    impMesh.name = 'tree-impostor';
    impMesh.castShadow = false; impMesh.receiveShadow = false;
    impMesh.frustumCulled = false; // spans the streamed area; the per-instance distance fade handles culling
    for (let i = 0; i < count; i++) {
      const node = nodes[i];
      node.impostor = impMesh;
      this.tmp.compose(node.pos, node.quat, TMP_SCALE.set(node.fullScale, node.fullScale, node.fullScale));
      impMesh.setMatrixAt(i, this.tmp);
    }
    impMesh.instanceMatrix.needsUpdate = true;
    impMesh.count = count;
    this.group.add(impMesh);
    this.impostorMeshes.push({ mesh: impMesh, nodes });
  }

  // 0 night .. 1 day -- dims the unlit billboards so distant trees darken with the cycle like the 3D ones.
  setImpostorDay(day: number): void { this.uImpDay.value = day; }
  // Per-frame sun+ambient colour: the billboards multiply by this so they warm at dusk, cool at night and dim
  // under cloud like the lit 3D trees, instead of floating bright + green. Set from main off the sky/sun light.
  setImpostorTint(r: number, g: number, b: number): void { this.uImpTint.value.setRGB(r, g, b); }

  // Build a per-species far-LOD billboard material from a captured LOD texture: a camera-facing (Y-axis)
  // quad that scales in from nothing (inside impostorNear) to full (by impostorFar) off camera distance, and
  // dims with the day-night cycle. Unlit (the texture bakes the lighting) + fog so distance melts to the sky.
  private makeBillboardMaterial(url: string): THREE.MeshBasicMaterial {
    const tex = new THREE.TextureLoader().load(url);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.5, side: THREE.DoubleSide, fog: true });
    const uNear = this.uImpNear, uFar = this.uImpFar, uDay = this.uImpDay, uCtr = this.uImpCtr, uTint = this.uImpTint;
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uImpNear = uNear; shader.uniforms.uImpFar = uFar; shader.uniforms.uImpDay = uDay; shader.uniforms.uImpCtr = uCtr; shader.uniforms.uImpTint = uTint;
      shader.vertexShader = 'uniform float uImpNear;\nuniform float uImpFar;\nuniform vec2 uImpCtr;\nvarying float vImpDist;\n' + shader.vertexShader.replace(
        '#include <project_vertex>',
        `vec3 iwp = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
         float iscale = length(instanceMatrix[0].xyz);
         vec3 toCam = vec3(cameraPosition.x - iwp.x, 0.0, cameraPosition.z - iwp.z);
         float tl = length(toCam); toCam = tl > 0.0001 ? toCam / tl : vec3(0.0, 0.0, 1.0);
         vec3 bbRight = vec3(-toCam.z, 0.0, toCam.x);
         // FULL SIZE always (no shrink); the dissolve happens via dither in the fragment.
         vec3 bbWorld = iwp + (bbRight * transformed.x + vec3(0.0, 1.0, 0.0) * transformed.y) * iscale;
         vec4 mvPosition = viewMatrix * vec4(bbWorld, 1.0);
         gl_Position = projectionMatrix * mvPosition;
         // Fade by distance to the FOCUS (player) XZ -- the same metric the 3D streamer + the 3D fade-in use,
         // so billboard-out and 3D-in line up exactly (no holes, no double-tree) regardless of camera zoom.
         vImpDist = distance(iwp.xz, uImpCtr);`,
      );
      shader.fragmentShader = `uniform float uImpDay;\nuniform float uImpNear;\nuniform float uImpFar;\nuniform vec3 uImpTint;\nvarying float vImpDist;\n${IMP_DITHER_GLSL}\n` + shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
         // Dithered cross-FADE (no shrink, no pop): solid past impostorFar, dissolves out toward impostorNear
         // where the full 3D tree dithers IN (complement of this test) -- a screen-door cross-fade, not a scale.
         if (impDither(gl_FragCoord.xy) > smoothstep(uImpNear, uImpFar, vImpDist)) discard;
         // MATCH THE SCENE: multiply by the live sun+ambient tint (warms/cools/dims with the sky) + the day dim,
         // then DARKEN PROGRESSIVELY WITH DISTANCE (near-full by 300m, sinking toward the haze by ~900m) so the
         // far treeline settles into the horizon instead of floating bright + green. (Fog on the material then
         // melts the very farthest into the sky.)
         diffuseColor.rgb *= 1.18 * uImpTint * mix(0.06, 1.0, clamp(uImpDay, 0.0, 1.0)); // +18%: baked PNGs read too dark in-scene (owner)
         diffuseColor.rgb *= mix(1.0, 0.78, smoothstep(300.0, 900.0, vImpDist)); // was 0.5: far trees went near-black`,
      );
    };
    mat.customProgramCacheKey = () => 'tree-billboard5-tint';
    return mat;
  }

  // Sync one tree's impostor instance: hidden (degenerate) when chopped, else its mature transform. Cheap
  // (one matrix write); called only on chop/regrow/regenerate, never per frame.
  private writeImpostor(node: NodeInst, hidden: boolean): void {
    const imp = node.impostor;
    if (!imp) return;
    if (hidden) this.tmp.makeScale(0, 0, 0);
    else this.tmp.compose(node.pos, node.quat, TMP_SCALE.set(node.fullScale, node.fullScale, node.fullScale));
    imp.setMatrixAt(node.index, this.tmp);
    imp.instanceMatrix.needsUpdate = true;
  }

  private register(mesh: THREE.InstancedMesh, nodes: NodeInst[]): void {
    this.meshes.push(mesh);
    this.byMesh.set(mesh, nodes);
    this.visibleByMesh.set(mesh, nodes); // ore is static: draw slot == index, so the visible list is the node list
    this.group.add(mesh);
  }

  // Harvest + growth + ore write through the node's CURRENT draw slot (ore: fixed == index; trees: the
  // compacted stream slot). A culled tree (slot < 0) has no slot to write -- the next stream pass redraws it.
  private writeMatrix(node: NodeInst, factor: number): void {
    if (node.slot < 0) return;
    this.writeMatrixAt(node, node.slot, factor);
  }

  // Scatter a TREE, biome-aware: dense FOREST POCKETS (the forest-density field, made contrasty so
  // open ground stays bare), thinned out on the open lowland flats + the shoreline, peaking in the
  // hills, and fading high on mountains (so peaks are sparse but the base still has trees). Falls back
  // to the last on-land candidate if 40 tries do not accept.
  private scatterTree(inner: number): [number, number] {
    let x = 0, z = 0, landX = 0, landZ = 0, gotLand = false;
    const sea = TERRAIN.seaLevel;
    for (let tries = 0; tries < 40; tries++) {
      const angle = this.rng() * Math.PI * 2;
      const dist = inner + this.rng() * (NODES.scatterRadius - inner);
      x = Math.cos(angle) * dist;
      z = Math.sin(angle) * dist;
      const h = terrainHeight(x, z);
      if (h <= sea + 2.5) continue; // off the immediate waterline
      landX = x; landZ = z; gotLand = true;
      const d = forestDensity(x, z); // pockets
      // Elevation band: ~0 on the shore + dead-flat lowland, ~1 through the hills, fading on mountains.
      const band = smoothstep01(sea + 3, sea + 25, h) * (1 - smoothstep01(TERRAIN.snowStart * 0.7, TERRAIN.snowEnd, h));
      // Forest cover ramps to ~full inside a pocket, but open ground keeps a real baseline now (was nearly
      // bare) so grassland/meadow gets scattered lone trees instead of odd empty stretches.
      // Strong open-ground baseline so grasslands are forested; deep forest pockets get ~2x on top (the
      // (1 + smoothstep) factor) so forests read TWICE as dense as the open ground, per the design ask.
      const prob = (0.28 + 0.72 * smoothstep01(0.40, 0.72, d)) * (0.5 + 0.5 * band) * (1.0 + smoothstep01(0.5, 0.82, d));
      // Steep slopes: trees grow RARE, not impossible. slope is rise/run (~0.58 = 30 deg, ~0.84 = 40 deg);
      // the penalty falls from 1 on gentle ground toward a 0.1 floor on cliffs, so the odd tree still clings.
      const slopePenalty = 0.1 + 0.9 * (1 - smoothstep01(0.58, 0.84, terrainSlope(x, z, 1.2)));
      if (this.rng() < prob * slopePenalty) return [x, z];
    }
    return gotLand ? [landX, landZ] : [x, z];
  }

  // Scatter an ORE node on HIGHER ground (hills + mountains), with a small baseline so some appear on
  // lower ground too. Used for stone (common, biased uphill).
  private scatterHigh(inner: number): [number, number] {
    let x = 0, z = 0, landX = 0, landZ = 0, gotLand = false;
    const sea = TERRAIN.seaLevel;
    for (let tries = 0; tries < 40; tries++) {
      const angle = this.rng() * Math.PI * 2;
      const dist = inner + this.rng() * (NODES.scatterRadius - inner);
      x = Math.cos(angle) * dist;
      z = Math.sin(angle) * dist;
      const h = terrainHeight(x, z);
      if (h <= sea + 3.5) continue; // ore stays out of the water / off the shoreline
      landX = x; landZ = z; gotLand = true;
      const high = smoothstep01(sea + 8, sea + 45, h); // a touch more likely the higher you go
      // Mostly EVEN now: a high baseline so stone is findable in every biome, with only a gentle uphill +
      // stoneBias lean on top (the owner wanted ore accessible everywhere, not hoarded on the peaks).
      const sb = BIOMES[biomeAt(x, z, h)].stoneBias;
      const accept = Math.min(1, (0.5 + 0.35 * high) * (0.7 + 0.22 * sb));
      if (this.rng() < accept) return [x, z];
    }
    return gotLand ? [landX, landZ] : [x, z];
  }

  // Scatter an ORE node tightly around one of a few rare cluster centres (so it spawns in clumps,
  // not spread evenly). Used for sulfur. Cluster centres are picked on higher ground and cached.
  private scatterCluster(): [number, number] {
    if (!this.sulfurClusters.length) {
      for (let i = 0; i < 9; i++) this.sulfurClusters.push(this.scatterHigh(40)); // more findable sulfur spots (was 5), still clustered
    }
    const c = this.sulfurClusters[(this.rng() * this.sulfurClusters.length) | 0];
    for (let tries = 0; tries < 20; tries++) {
      const a = this.rng() * Math.PI * 2;
      const r = this.rng() * 32;
      const x = c[0] + Math.cos(a) * r;
      const z = c[1] + Math.sin(a) * r;
      if (terrainHeight(x, z) > TERRAIN.seaLevel + 3.5) return [x, z]; // sulfur stays off the shoreline
    }
    return [c[0], c[1]];
  }

  // Route each ore type to its placement style: stone uphill, sulfur clustered, metal scattered evenly.
  private oreScatter(item: ItemId, inner: number): [number, number] {
    if (item === 'stone') return this.scatterHigh(inner);
    if (item === 'sulfur') return this.scatterCluster();
    return this.scatter(inner);
  }

  // Scatter in a ring so nodes do not pile on the spawn point.
  // Scatter on a ring, retrying until the spot is on dry land (above the waterline) so trees and
  // ore never spawn in lakes or the sea.
  private scatter(inner: number): [number, number] {
    let x = 0, z = 0;
    for (let tries = 0; tries < 30; tries++) {
      const angle = this.rng() * Math.PI * 2;
      // EVEN BY AREA (sqrt): linear-in-radius piled metal toward the centre (dense core, sparse coast). This
      // spreads metal ore uniformly across the whole island so it's findable everywhere, not hoarded inland.
      const dist = Math.sqrt(inner * inner + this.rng() * (NODES.scatterRadius * NODES.scatterRadius - inner * inner));
      x = Math.cos(angle) * dist;
      z = Math.sin(angle) * dist;
      if (terrainHeight(x, z) > TERRAIN.seaLevel + 3.5) break; // ore stays off the shoreline
    }
    return [x, z];
  }
}

// A soft round glow for the harvest sweet-spot sprite (white-hot core, warm falloff).
export function makeGlowTexture(): THREE.CanvasTexture {
  const size = 64;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,236,170,0.95)');
  g.addColorStop(0.6, 'rgba(255,180,80,0.5)');
  g.addColorStop(1, 'rgba(255,160,60,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// A rounded, lumpy boulder (stone/metal/sulfur ore), squashed wider-than-tall and partly
// buried so it reads like the embedded Rust rock nodes. CREASED normals (sharp joins keep hard
// edges) read as chipped stone; a baked dark-base vertex gradient fakes the ground contact shadow.
function makeBoulderGeometry(): THREE.BufferGeometry {
  const geo = new THREE.IcosahedronGeometry(1, 2);
  const pos = geo.attributes.position;
  const v = new THREE.Vector3();
  let minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i).normalize();
    const lump =
      0.16 * Math.sin(v.x * 3.1 + 1.0) * Math.sin(v.y * 2.7) +
      0.10 * Math.sin(v.z * 4.2 + 2.0) +
      0.07 * Math.cos(v.x * 5.5 + v.z * 3.3);
    v.multiplyScalar(1 + lump);
    v.y = v.y * 0.82 - 0.05; // wider than tall, sit a touch low
    pos.setXYZ(i, v.x, v.y, v.z);
    if (v.y < minY) minY = v.y;
    if (v.y > maxY) maxY = v.y;
  }
  pos.needsUpdate = true;
  creasedNormals(geo, 30); // non-indexed in-place: hard facet edges, smooth flats (no smooth-blob look)
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - minY) / (maxY - minY);
    const c = 0.4 + 0.6 * t; // dark contact base, full-bright crown
    colors[i * 3] = c; colors[i * 3 + 1] = c; colors[i * 3 + 2] = c;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}

// --- Trees: two instanced shapes, each a solid trunk + alpha-cut leaf-card foliage ---

const LEAF_TEX = makeLeafTexture();
const LEAF_WIND = { value: 0 }; // updated each frame by NodeField.update -> gentle canopy sway
const TRUNK_MAT = new THREE.MeshStandardMaterial({ color: 0x7a5c3c, roughness: 0.92, metalness: 0, flatShading: true });

// --- Apple trees (oak/aspen only) -------------------------------------------------------------------
// Ripe-apple red, a touch glossy so the fruit pops out of the matte canopy.
const APPLE_MAT = new THREE.MeshStandardMaterial({ color: 0xc4392f, roughness: 0.42, metalness: 0 });
// Which trees bear fruit: a stable hash of the trunk's world XZ, so the SAME oaks/aspens have apples on every
// client and every boot -- WITHOUT drawing from the layout rng (that would shift all downstream world gen + break saves).
function hashApple(x: number, z: number): boolean {
  const h = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return (h - Math.floor(h)) < NODES.appleTreeChance;
}
// A clump of ~8 apples sitting in the lower-outer half of THIS species' canopy volume (bbox in the tree's local,
// pre-scale space, so one instance matrix places + scales the whole crop with the tree). Fixed shape across all
// apple trees; the per-tree quaternion rotates it so they don't look cloned. Spheres are indexed like the tree
// parts (mergeGeometries needs all-or-none indexing).
function makeAppleClusterGeo(canopy: THREE.Box3): THREE.BufferGeometry {
  const cx = (canopy.min.x + canopy.max.x) / 2, cz = (canopy.min.z + canopy.max.z) / 2, cy = (canopy.min.y + canopy.max.y) / 2;
  const rx = (canopy.max.x - canopy.min.x) * 0.5, rz = (canopy.max.z - canopy.min.z) * 0.5, ry = (canopy.max.y - canopy.min.y) * 0.5;
  const rng = mulberry32(0x4ea71e);
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 8; i++) {
    const a = rng() * Math.PI * 2;
    const rad = 0.5 + rng() * 0.48;                 // toward the canopy edge where fruit hangs
    const s = new THREE.SphereGeometry(0.055, 6, 5); // smaller apples (owner)
    s.translate(cx + Math.cos(a) * rx * rad, cy - ry * (0.05 + rng() * 0.7), cz + Math.sin(a) * rz * rad);
    parts.push(s);
  }
  const g = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  return g;
}

// vertexColors: each canopy card carries a top-light gradient (bright crown, shaded underside) baked in
// makeCanopy; it multiplies with the per-tree instanceColor tint + the leaf texture for a 3D, lit crown.
const LEAF_MAT = new THREE.MeshStandardMaterial({ map: LEAF_TEX, alphaTest: 0.42, side: THREE.DoubleSide, roughness: 0.78, metalness: 0, vertexColors: true });
LEAF_MAT.onBeforeCompile = (shader) => {
  shader.uniforms.uWind = LEAF_WIND;
  shader.uniforms.uWindDir = WIND_DIR;   // XZ direction the canopy leans
  shader.uniforms.uWindGust = WIND_GUST; // gust strength scales the sway amplitude
  shader.uniforms.uSnowLo = { value: 58.0 }; // snow starts fading onto leaf-tops above this world height...
  shader.uniforms.uSnowHi = { value: 92.0 }; // ...full by here (snow biomes are the high, cold ground)
  shader.vertexShader = 'uniform float uWind;\nuniform vec2 uWindDir;\nuniform float uWindGust;\nvarying float vSnowUp;\nvarying float vSnowY;\n' + shader.vertexShader.replace(
    '#include <begin_vertex>',
    `#include <begin_vertex>
    {
      vec3 ip = (instanceMatrix * vec4(transformed, 1.0)).xyz;
      // Stop the canopy sway past ~300m from the camera (distant trees do not need the per-vertex wobble).
      float swayFade = 1.0 - smoothstep(270.0, 300.0, distance(ip, cameraPosition));
      // Flutter (the oscillation) PLUS a steady lean along the wind direction, all scaled by the live gust so
      // a calm breeze is the old gentle wobble and a hard gust pushes the whole canopy over.
      float osc = sin(uWind * 1.3 + ip.x * 0.3 + ip.z * 0.25);
      float amp = 0.05 * max(0.0, transformed.y - 1.0) * swayFade * (0.35 + uWindGust);
      transformed.x += (osc * 0.55 + uWindDir.x * 0.6) * amp;
      transformed.z += (osc * 0.55 + uWindDir.y * 0.6) * amp * 0.9;
      // SNOW: pass this leaf card's world up-facing amount + world height to the fragment (see below).
      #ifdef USE_INSTANCING
        vSnowUp = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * objectNormal).y;
        vSnowY = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).y;
      #else
        vSnowUp = normalize(mat3(modelMatrix) * objectNormal).y;
        vSnowY = (modelMatrix * vec4(transformed, 1.0)).y;
      #endif
    }`,
  );
  shader.fragmentShader = 'varying float vSnowUp;\nvarying float vSnowY;\nuniform float uSnowLo;\nuniform float uSnowHi;\n' + shader.fragmentShader.replace(
    '#include <color_fragment>',
    `#include <color_fragment>
    {
      // SNOW on snowy-biome tree tops (owner): white settles on UP-facing leaf cards above the snow-line
      // altitude (snow biomes are the high/cold ground), easing in over uSnowLo..uSnowHi so the treeline reads
      // natural. Side/underside leaves stay green -- snow sits on the TOP of the canopy only.
      float oxSnow = smoothstep(0.15, 0.55, vSnowUp) * smoothstep(uSnowLo, uSnowHi, vSnowY);
      diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.93, 0.95, 0.98), oxSnow * 0.85);
    }`,
  );
};
LEAF_MAT.customProgramCacheKey = () => 'leaf-wind-snow-2-winddir';
// TRUNK SWAY: the whole tree flexes gently from the base (amplitude grows with height), so bare DEAD
// trees and palm trunks move in the wind like the canopies do. Same 300m fade as the leaf sway.
function applyTrunkSway(mat: THREE.Material, amp: number, key: string): void {
  const prevCompile = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.onBeforeCompile = (shader, renderer) => {
    prevCompile.call(mat, shader, renderer);
    shader.uniforms.uTrunkWind = LEAF_WIND;
    shader.vertexShader = 'uniform float uTrunkWind;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      {
        vec3 oxTip = transformed;
        #ifdef USE_INSTANCING
          oxTip = (instanceMatrix * vec4(transformed, 1.0)).xyz;
        #endif
        float oxTFade = 1.0 - smoothstep(270.0, 300.0, distance(oxTip, cameraPosition));
        float oxFlex = sin(uTrunkWind * 0.9 + oxTip.x * 0.21 + oxTip.z * 0.17) * ${amp.toFixed(3)} * max(0.0, transformed.y - 0.8) * oxTFade;
        transformed.x += oxFlex;
        transformed.z += oxFlex * 0.55;
      }`,
    );
  };
  mat.customProgramCacheKey = () => prevKey.call(mat) + '|trunksway1-' + key;
}
applyTrunkSway(TRUNK_MAT, 0.014, 'trunk'); // subtle flex on every tree (dead trees finally move); palms get theirs below

// Trees obey the same base-interior darken + look-cone as the ground, so inside a base they go dark
// and only the cone out a window/door reveals them (instanced -> fold in instanceMatrix).
applyInteriorDark(TRUNK_MAT, true);
applyInteriorDark(LEAF_MAT, true);
applyImpostorFadeIn(TRUNK_MAT); // dither the full 3D tree IN as the billboard dithers OUT (cross-fade, no pop)
applyImpostorFadeIn(LEAF_MAT);
const LEAF_DEPTH = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: LEAF_TEX, alphaTest: 0.5 });

// Per-biome tree species. Most reuse the EZ-tree presets; dead is a bare trunk (no foliage) and cactus is
// a green column (a stylised saguaro; arms can come later).
export type TreeSpecies = 'pine' | 'oak' | 'aspen' | 'snowpine' | 'cactus' | 'dead' | 'palm';
export const TREE_SPECIES: readonly TreeSpecies[] = ['pine', 'oak', 'aspen', 'snowpine', 'dead', 'cactus', 'palm'];

// Build a single, display-ready tree of a species (real EZ-tree geometry + the in-game materials, a
// representative tint), centred at the origin with its base at y=0. Used by the /admin LOD capture tool so
// the captured billboard image matches the in-world tree. Not instanced -- one standalone tree.
export function buildDisplayTree(sp: TreeSpecies): THREE.Group {
  const g = new THREE.Group();
  const parts = treeParts(sp);
  g.add(new THREE.Mesh(parts.trunk, (sp === 'cactus' ? CACTUS_MAT : sp === 'palm' ? PALM_TRUNK_MAT : TRUNK_MAT).clone()));
  if (parts.foliage) {
    const folMat = (sp === 'palm' ? PALM_FROND_MAT : LEAF_MAT).clone();
    folMat.color.set(sp === 'snowpine' ? 0xdfe7f2 : sp === 'palm' ? 0x4f8f44 : 0x77a558); // a typical canopy tint
    g.add(new THREE.Mesh(parts.foliage, folMat));
  }
  return g;
}
// Cactus skin: faint mottle + rows of pale spine-dot areoles (the reference photos' defining read).
function makeCactusTexture(): THREE.CanvasTexture {
  const w = 64, h = 128;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = 0.80 + 0.10 * Math.sin(x * 0.9 + y * 0.23) * Math.sin(y * 0.41); // soft skin mottle
      // spine areoles: a 8x16 dot grid, offset every other row
      const gx = ((x + (Math.floor(y / 8) % 2) * 4) % 8) - 4, gy = (y % 8) - 4;
      if (gx * gx + gy * gy < 2.6) v = 1.0; // pale dot
      const i = (y * w + x) * 4;
      img.data[i] = v * 0.88 * 255; img.data[i + 1] = v * 255; img.data[i + 2] = v * 0.86 * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}
const CACTUS_MAT = new THREE.MeshStandardMaterial({ map: makeCactusTexture(), color: 0x4f7d3e, roughness: 0.85, metalness: 0, flatShading: true, vertexColors: true }); // vertexColors: the pale blooms ride in the geometry

// Palm bark: wobbled horizontal growth-ring bands + vertical fiber streaks (canvas, boot-once).
function makePalmBarkTexture(): THREE.CanvasTexture {
  const w = 64, h = 128;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ring = 0.5 + 0.5 * Math.sin((y / h) * Math.PI * 2 * 9 + Math.sin(x * 0.42) * 0.9); // 9 wobbled bands per tile
      const fiber = 0.5 + 0.5 * Math.sin(x * 1.7 + y * 0.13);
      let v = 0.50 + 0.32 * Math.pow(ring, 1.6); // pale band tops, shadowed undersides
      v *= 0.90 + 0.16 * fiber;
      const i = (y * w + x) * 4;
      img.data[i] = Math.min(1, v * 0.78) * 255;
      img.data[i + 1] = Math.min(1, v * 0.64) * 255;
      img.data[i + 2] = Math.min(1, v * 0.48) * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(1, 3); // ~27 ring bands over a trunk
  t.anisotropy = 4;
  return t;
}
// The palm trunk is textured bark (rings + fiber), unlike the generic flat-brown tree trunk.
const PALM_TRUNK_MAT = new THREE.MeshStandardMaterial({ map: makePalmBarkTexture(), color: 0xffffff, roughness: 0.88, metalness: 0 });

// Palm frond leaflets: bright angled stripes off a pale midrib, darker toward the serrated edges.
// Drawn NEUTRAL-bright: the per-instance foliage tint supplies the actual green.
function makePalmFrondTexture(): THREE.CanvasTexture {
  const w = 128, h = 64;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const vAcross = Math.abs(y / h - 0.5) * 2; // 0 at the midrib, 1 at the edges
      const stripe = 0.5 + 0.5 * Math.sin(x * 0.85 + vAcross * 2.4); // leaflet stripes, angled off the rib
      let v = 0.68 + 0.30 * Math.pow(stripe, 1.4);
      v *= 1.0 - 0.28 * vAcross * vAcross;              // edges tuck darker
      if (vAcross < 0.10) v = Math.max(v, 0.98);        // the pale midrib line
      const i = (y * w + x) * 4;
      img.data[i] = v * 0.90 * 255; img.data[i + 1] = v * 255; img.data[i + 2] = v * 0.82 * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}
applyInteriorDark(CACTUS_MAT, true);
applyImpostorFadeIn(CACTUS_MAT);
applyTrunkSway(PALM_TRUNK_MAT, 0.03, 'palm');   // palms lean into the wind harder
applyInteriorDark(PALM_TRUNK_MAT, true);
applyImpostorFadeIn(PALM_TRUNK_MAT);
// Palm fronds: SOLID double-sided ribbons (no alpha leaf texture; the blob leaf does not suit a long frond).
// White base colour so the per-tree instanceColor green tint + the top-light vertex colour set the look; the
// same gentle wind sway as the canopy. Its own cache key so the shader does not collide with LEAF_MAT.
const PALM_FROND_MAT = new THREE.MeshStandardMaterial({ map: makePalmFrondTexture(), color: 0xffffff, side: THREE.DoubleSide, roughness: 0.7, metalness: 0, vertexColors: true });
PALM_FROND_MAT.onBeforeCompile = (shader) => {
  shader.uniforms.uWind = LEAF_WIND;
  shader.vertexShader = 'uniform float uWind;\n' + shader.vertexShader.replace(
    '#include <begin_vertex>',
    `#include <begin_vertex>
    {
      vec3 ip = (instanceMatrix * vec4(transformed, 1.0)).xyz;
      float swayFade = 1.0 - smoothstep(270.0, 300.0, distance(ip, cameraPosition));
      float sway = sin(uWind * 1.1 + ip.x * 0.3 + ip.z * 0.25) * 0.07 * max(0.0, transformed.y - 1.5) * swayFade;
      transformed.x += sway;
      transformed.z += sway * 0.6;
    }`,
  );
};
PALM_FROND_MAT.customProgramCacheKey = () => 'palm-frond';
applyInteriorDark(PALM_FROND_MAT, true);
applyImpostorFadeIn(PALM_FROND_MAT);
// Sun-projected cloud shadows on the near 3D trees -> canopy + trunks darken under the same drifting clouds as
// the ground. Applied AFTER each material's other onBeforeCompile setups so it composes (chains) with them.
applyCloudShadow(TRUNK_MAT, 'trunk');
applyCloudShadow(LEAF_MAT, 'leaf');
applyTrunkSway(PALM_FROND_MAT, 0.03, 'fronds'); // the crown rides the SAME trunk flex so it stays socketed (leaf-sway adds on top)
applyCloudShadow(CACTUS_MAT, 'cactus');
applyCloudShadow(PALM_FROND_MAT, 'palm');
applyCloudShadow(PALM_TRUNK_MAT, 'palmtrunk');
// Display-ready rock / ore boulder for the /admin LOD tool (real boulder geometry + a representative tint).
export type NodeKind = 'rock' | 'stone' | 'metal' | 'sulfur';
export const NODE_KINDS: readonly NodeKind[] = ['rock', 'stone', 'metal', 'sulfur'];
export function buildDisplayNode(kind: NodeKind): THREE.Group {
  const g = new THREE.Group();
  const color = kind === 'metal' ? 0x9c7b52 : kind === 'sulfur' ? 0xc9b94a : kind === 'stone' ? 0xc4c8cc : 0x8a8f96;
  const mat = new THREE.MeshStandardMaterial({
    color, vertexColors: true, // boulder geo bakes a dark contact base -> lighter crown
    roughness: kind === 'metal' ? 0.5 : 0.9, metalness: kind === 'metal' ? 0.4 : 0,
  });
  g.add(new THREE.Mesh(makeBoulderGeometry(), mat));
  return g;
}

// --- cacti: fluted (ribbed) columns like a real saguaro, three variants ------------------------------

// A ribbed cactus column with a domed top: cylinder verts pulled in/out around the angle (the flutes),
// optional barrel belly. Base at y=0. The dome is the sphere-capped top row, fluted the same way.
function flutedColumn(rBase: number, rTop: number, h: number, ribs: number, ribDepth: number, bulge = 0): THREE.BufferGeometry {
  const radial = 24, hSegs = 6;
  const body = new THREE.CylinderGeometry(1, 1, h, radial, hSegs, true);
  const dome = new THREE.SphereGeometry(1, radial, 4, 0, Math.PI * 2, 0, Math.PI / 2);
  dome.scale(1, 0.55, 1);
  dome.translate(0, h / 2, 0);
  const g = mergeGeometries([body, dome], false)!;
  body.dispose(); dome.dispose();
  const pos = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const t = Math.min(1, Math.max(0, (v.y + h / 2) / h));
    let r = rBase + (rTop - rBase) * t;
    if (bulge > 0) r *= 1 + bulge * Math.sin(Math.PI * t); // barrel belly
    const a = Math.atan2(v.z, v.x);
    const flute = 1 - ribDepth * (0.5 + 0.5 * Math.cos(a * ribs));
    const rr = Math.hypot(v.x, v.z); // dome rows shrink toward the crown; scale their own radius instead
    const scale = (v.y > h / 2 - 1e-4 ? rr : 1) * r * flute;
    pos.setXYZ(i, Math.cos(a) * scale, v.y, Math.sin(a) * scale);
  }
  pos.needsUpdate = true;
  g.translate(0, h / 2, 0); // base at y=0
  g.computeVertexNormals();
  return g;
}

// Paint a flat vertex colour on a geometry (CACTUS_MAT is vertexColors so flower buds can read pale).
function paintFlat(g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return g;
}

// Three cactus looks, matched to the owner's reference photos -- CLUSTERED vertical ribbed columns
// (San Pedro style), no side-arms: (0) a tight family cluster (tall centre, ring of shorter pups),
// (1) one slim sentinel column, (2) a squat barrel trio. People-sized plants; base at y=0.
function makeCactusGeom(variant: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const bloom = (x: number, y: number, z: number, r: number): void => {
    const b = new THREE.SphereGeometry(r, 7, 5);
    b.translate(x, y, z);
    parts.push(paintFlat(b, 0xe8c9d6)); // pale desert bloom
  };
  const col = (r: number, h: number, x: number, z: number, ribs = 9): void => {
    const c = flutedColumn(r, r * 0.86, h, ribs, 0.12);
    c.translate(x, 0, z);
    parts.push(paintFlat(c, 0xffffff));
  };
  if (variant === 0) {
    // the reference cluster: parallel columns of varying heights bunched at the base
    col(0.15, 1.45, 0, 0, 10);
    col(0.125, 1.10, 0.235, 0.05);
    col(0.115, 0.90, -0.21, 0.11);
    col(0.105, 0.70, 0.09, -0.21);
    col(0.095, 0.52, -0.15, -0.17);
    col(0.085, 0.40, 0.30, -0.10, 8);
    bloom(0, 1.49, 0, 0.05); bloom(0.235, 1.13, 0.05, 0.035);
  } else if (variant === 1) {
    // one slim ribbed sentinel (the potted-column reference)
    col(0.105, 1.05, 0, 0, 11);
  } else {
    // barrel trio with blooms
    parts.push(paintFlat(flutedColumn(0.28, 0.21, 0.60, 13, 0.14, 0.22), 0xffffff));
    const pupA = flutedColumn(0.15, 0.115, 0.32, 11, 0.14, 0.2); pupA.translate(0.34, 0, 0.10); parts.push(paintFlat(pupA, 0xffffff));
    const pupB = flutedColumn(0.115, 0.09, 0.24, 9, 0.14, 0.2); pupB.translate(-0.27, 0, -0.19); parts.push(paintFlat(pupB, 0xffffff));
    bloom(0, 0.66, 0, 0.055); bloom(0.34, 0.36, 0.10, 0.038);
  }
  const g = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  return g;
}

// --- palms: curved ringed trunks + serrated drooping fronds, three variants --------------------------

// A palm trunk that CURVES: cylinder verts sheared along +X by bend*t^2 (t = height fraction), with
// growth-ring bumps along the height and a slight base flare. Returns the trunk and where the crown
// ended up (the fronds/coconuts attach there). Base at y=0.
function curvedTrunk(h: number, rBase: number, rTop: number, bend: number): { geo: THREE.BufferGeometry; crownX: number } {
  const g = new THREE.CylinderGeometry(1, 1, h, 7, 10);
  const pos = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const t = Math.min(1, Math.max(0, (v.y + h / 2) / h));
    let r = rBase + (rTop - rBase) * t;
    r *= 1 + 0.06 * (0.5 + 0.5 * Math.cos(t * 26)); // growth-ring bumps
    r *= 1 + 0.5 * Math.max(0, 0.12 - t);            // base flare
    const a = Math.atan2(v.z, v.x);
    pos.setXYZ(i, Math.cos(a) * r + bend * t * t, v.y, Math.sin(a) * r);
  }
  pos.needsUpdate = true;
  g.translate(0, h / 2, 0);
  g.computeVertexNormals();
  return { geo: g, crownX: bend };
}

// A palm: curved trunk + fountain crown of serrated fronds + a fiber skirt, a crown BOSS the fronds
// visibly socket into, and a coconut cluster (all trunk-geometry, so they pick up the bark texture/tint).
// The three variants read differently on purpose: medium classic / tall OPEN droopy leaner / young
// stout CLOSED upright crown.
function makePalmParts(variant: number): { trunk: THREE.BufferGeometry; foliage: THREE.BufferGeometry } {
  const cfg = [
    { h: 3.4, rB: 0.17, rT: 0.09, bend: 0.5, fronds: 11, len: 2.2, a0: 0.5, a1: -1.2, nuts: 3 },   // the classic
    { h: 4.3, rB: 0.15, rT: 0.08, bend: 0.95, fronds: 9, len: 2.6, a0: 0.35, a1: -1.6, nuts: 2 },  // tall leaner, OPEN droopy crown
    { h: 2.0, rB: 0.20, rT: 0.12, bend: 0.2, fronds: 14, len: 1.6, a0: 0.9, a1: -0.45, nuts: 0 },  // young stout, CLOSED upright crown
  ][variant] ?? { h: 3.4, rB: 0.17, rT: 0.09, bend: 0.5, fronds: 11, len: 2.2, a0: 0.5, a1: -1.2, nuts: 3 };
  const tParts: THREE.BufferGeometry[] = [];
  const { geo: trunkGeo, crownX } = curvedTrunk(cfg.h, cfg.rB, cfg.rT, cfg.bend);
  tParts.push(trunkGeo);
  const boss = new THREE.SphereGeometry(cfg.rT * 1.35, 8, 6); // the crown boss every frond sockets into (subtle, not a bulb)
  boss.scale(1, 0.75, 1);
  boss.translate(crownX, cfg.h, 0);
  tParts.push(boss);
  const skirt = new THREE.CylinderGeometry(cfg.rT * 1.05, cfg.rT * 1.9, 0.28, 8, 1, true); // dead-frond fiber tuck (was a lampshade)
  skirt.translate(crownX, cfg.h - 0.16, 0);
  tParts.push(skirt);
  for (let i = 0; i < cfg.nuts; i++) { // coconut cluster tucked under the crown
    const a = i * 2.4;
    const nut = new THREE.SphereGeometry(0.135, 6, 5); // bigger coconuts (owner) -- indexed like the cylinders (mergeGeometries needs all-or-none)
    nut.translate(crownX + Math.cos(a) * 0.16, cfg.h - 0.30, Math.sin(a) * 0.16);
    tParts.push(nut);
  }
  const trunk = mergeGeometries(tParts, false)!;
  for (const p of tParts) p.dispose();
  const fronds: THREE.BufferGeometry[] = [];
  for (let i = 0; i < cfg.fronds; i++) {
    const a = (i / cfg.fronds) * Math.PI * 2 + 0.2;
    const len = cfg.len + (i % 3) * 0.12;                       // a little length variation around the crown
    const lift = cfg.a0 + ((i % 3) - 1) * 0.07;                 // staggered lift angles so the crown isn't a perfect fan
    const droop = cfg.a1 - (i % 2) * 0.16;                      // alternate fronds hang lower
    fronds.push(makePalmFrond(len, a, cfg.h - 0.03, crownX, lift, droop)); // start just inside the boss = visibly socketed
  }
  const foliage = mergeGeometries(fronds, false);
  if (!foliage) throw new Error('palm frond merge failed');
  for (const f of fronds) f.dispose();
  addFoliageTopLight(foliage); // brighten toward the arch top (PALM_FROND_MAT uses vertexColors)
  return { trunk, foliage };
}

// One palm frond: a tapered ribbon whose centreline arches UP off the crown then curves DOWN at the tip
// (tangent smoothstepped from a0 up-angle to a1 down-angle). The blade is V-FOLDED along its spine
// (edges sit below the midrib) and its width zigzags per segment (serrated leaflet read) -- both are
// what make it read as a palm frond instead of a smooth ribbon. yaw fans it around the trunk.
function makePalmFrond(L: number, yaw: number, crownY: number, crownX: number, a0: number, a1: number): THREE.BufferGeometry {
  const segs = 10; // smoother arc -- the droop reads as a curve, not a hinge
  const segLen = L / segs;
  const cx = [0], cy = [0];
  let px = 0, py = 0;
  for (let i = 1; i <= segs; i++) {
    const t = (i - 0.5) / segs;
    const ang = a0 + (a1 - a0) * (t * t * (3 - 2 * t)); // smoothstep the tangent from up to down
    px += Math.cos(ang) * segLen;
    py += Math.sin(ang) * segLen;
    cx.push(px); cy.push(py);
  }
  const wBase = 0.115, wTip = 0.010; // slim blades: a crown of ribbons, not paddles
  const pos = new Float32Array((segs + 1) * 3 * 3); // 3 verts per row: left edge, MIDRIB, right edge
  const uv = new Float32Array((segs + 1) * 3 * 2);  // u along the frond, v across (the leaflet texture reads off this)
  for (let i = 0; i <= segs; i++) {
    let w = wBase + (wTip - wBase) * (i / segs);
    if (i > 0 && i < segs) w *= i % 2 === 0 ? 1.0 : 0.68; // serrated: alternate rows pinch in
    const fold = w * 0.55;                                 // V-fold: edges drop below the midrib
    const o = i * 3 * 3;
    pos[o] = cx[i]; pos[o + 1] = cy[i] - fold; pos[o + 2] = -w;      // left edge
    pos[o + 3] = cx[i]; pos[o + 4] = cy[i]; pos[o + 5] = 0;          // midrib
    pos[o + 6] = cx[i]; pos[o + 7] = cy[i] - fold; pos[o + 8] = w;   // right edge
    const uo = i * 3 * 2, u = i / segs;
    uv[uo] = u; uv[uo + 1] = 0;
    uv[uo + 2] = u; uv[uo + 3] = 0.5;
    uv[uo + 4] = u; uv[uo + 5] = 1;
  }
  const idx: number[] = [];
  for (let i = 0; i < segs; i++) {
    const a = i * 3;
    idx.push(a, a + 3, a + 1, a + 1, a + 3, a + 4);       // left half
    idx.push(a + 1, a + 4, a + 2, a + 2, a + 4, a + 5);   // right half
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  g.rotateY(yaw);                   // fan around the trunk
  g.translate(crownX, crownY, 0);  // lift to the crown (which the curved trunk moved off-centre)
  return g;
}

// A top-light vertex-colour gradient on a foliage geometry (bright crown -> shaded base), so LEAF_MAT
// (vertexColors) reads it as a lit mass. Mirrors what eztrees does for its leaf cards.
function addFoliageTopLight(geo: THREE.BufferGeometry): void {
  geo.computeBoundingBox();
  const y0 = geo.boundingBox!.min.y;
  const y1 = Math.max(geo.boundingBox!.max.y, y0 + 0.001);
  const pos = geo.attributes.position;
  const cols = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - y0) / (y1 - y0);
    const b = 0.7 + 0.5 * t;
    cols[i * 3] = b; cols[i * 3 + 1] = b; cols[i * 3 + 2] = b * 0.96;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(cols, 3));
}

// A far-LOD billboard quad sized to a tree's vertical bounding box. The capture framed the tree to fill ~86%
// of the frame height centred on the bbox centre, so the quad spans 1.16x that range (frame margin) at a
// 0.75 (= CAP_W/CAP_H) aspect, centred on the bbox centre -> the textured tree's base + top line up with the
// 3D tree at the same per-instance scale, so the crossfade does not jump.
function makeBillboardQuad(minY: number, maxY: number): THREE.PlaneGeometry {
  const H = Math.max(0.001, maxY - minY);
  const quadH = H * 1.16, quadW = quadH * 0.75;
  const geo = new THREE.PlaneGeometry(quadW, quadH);
  geo.translate(0, (minY + maxY) * 0.5, 0);
  return geo;
}

// Species with more than one geometry look (a variant = its own InstancedMesh sharing the species'
// material, billboard and gameplay config; the scatter picks one per tree).
export const TREE_VARIANT_COUNT: Partial<Record<TreeSpecies, number>> = { cactus: 3, palm: 3 };

function treeParts(sp: TreeSpecies, variant = 0): { trunk: THREE.BufferGeometry; foliage: THREE.BufferGeometry | null } {
  switch (sp) {
    case 'pine': return makeEzTreeParts('Pine Medium', 1201, 4.8);
    case 'oak': return makeEzTreeParts('Oak Medium', 1202, 3.9);
    case 'aspen': return makeEzTreeParts('Aspen Medium', 1203, 4.3);
    case 'snowpine': return makeEzTreeParts('Pine Medium', 1207, 4.6);
    case 'dead': return { trunk: makeEzTreeParts('Oak Medium', 1209, 3.6).trunk, foliage: null };
    case 'cactus': return { trunk: makeCactusGeom(variant), foliage: null };
    case 'palm': return makePalmParts(variant);
  }
}

// A leaf-cluster card: clumps of leaves with see-through gaps (alpha-cut at render). Two passes -- a
// fuller mid-green body that defines the silhouette, then lighter highlight clusters on top so it reads
// as lit foliage, not a flat dark blob. Kept fairly BRIGHT/desaturated: the per-tree instanceColor tint
// supplies the actual green hue, so a dark texture would double-darken (the old "dark blob" problem).
function makeLeafTexture(): THREE.CanvasTexture {
  const size = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  const rng = mulberry32(7);
  const blobs = (n: number, base: [number, number, number], jitter: number, radMin: number, radSpan: number, spread: number): void => {
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const r = Math.pow(rng(), 0.7) * spread * size;
      const x = size / 2 + Math.cos(a) * r;
      const y = size / 2 + Math.sin(a) * r * 0.92;
      const rad = radMin + rng() * radSpan;
      const sh = 1 - jitter * rng();
      const cr = Math.floor(base[0] * sh), cg = Math.floor(base[1] * sh), cb = Math.floor(base[2] * sh);
      const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
      g.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
      g.addColorStop(0.65, `rgba(${cr},${cg},${cb},0.95)`);
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, rad, 0, Math.PI * 2);
      ctx.fill();
    }
  };
  blobs(64, [112, 158, 84], 0.4, 7, 12, 0.46);   // fuller mid-green body (defines the silhouette) -- brightened so canopies aren't near-black
  blobs(48, [168, 202, 118], 0.28, 4, 7, 0.42);  // lighter leaf-cluster highlights -> reads as lit, leafy
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
